import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Chess } from 'chess.js';
import { Chessboard } from 'react-chessboard';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  claimTimeout,
  getChessGame,
  getChessGameState,
  postChessMove,
  resignChessGame,
  offerDraw,
  claimDraw,
  declineDraw,
  offerTakeback,
  declineTakeback,
  abortChessGame,
  spectateGame,
  startComputerGame,
  rematchGame,
  acceptChallenge,
  declineChallenge,
  cancelChallenge,
  getIncomingChallenges,
  getChessChat,
  postChessChat,
  runCloudAnalysis,
  getCachedCloudAnalysis,
  shareChessAnalysis,
  getSharedChessAnalysis,
  getPlayingPrefs,
  updatePlayingPrefs,
  reportChessPlayer,
  requestClubReview,
} from '../../api/playing';
import PageHeaderSection from '../../components/layout/PageHeaderSection';
import Button from '../../components/common/Button';
import UserLink from '../../components/common/UserLink';
import { createStockfishWorker } from '../../playing/stockfishEngine';
import usePrefersReducedMotion from '../../playing/usePrefersReducedMotion';
import {
  ANALYSIS_PRESETS,
  cloudDtoToAnalysisState,
  evalBarPct,
  evalSparklinePath,
  formatEvalLabel,
  mistakePliesFromQualities,
  serverMovesToClubReview,
  summarizeAnalysis,
  toWhiteEval,
} from '../../playing/gameAnalysis';
import { subscribeChessGame, subscribeChessGameChat, subscribeChessGameAnalysis, subscribeIncomingChallenges } from '../../playing/chessSocket';
import { isPlayingMuted, playChessCue, setPlayingMuted } from '../../playing/chessSounds';
import { getUserId, getRoles, getToken, getActiveRole } from '../../utils/auth';
import {
  chessFromGamePayload,
  withViewerPerspective,
  clocksAdjustedFromMeta,
  buildTaggedPgn,
  pgnEventLabel,
  pgnTimeControlTag,
  pgnTerminationTag,
  mergeGameUpdate,
  boardPosKey,
  parseApiDateMs,
} from '../../playing/gameView';
import { useChessCoach } from '../../context/ChessCoachContext';
import GameBoardCoach from '../../components/playing/GameBoardCoach';
import MovePlyNav from '../../components/playing/MovePlyNav';
import { isLegalPremove, premoveOptionSquares, premoveSquareStyles } from '../../playing/premove';
import { resolveOwnPieceRetarget } from '../../playing/pieceSelection';
import { bookCandidates, bookReply, lookupOpening, warmOpeningBook } from '../../playing/openingBook';
import { localizeOpeningName } from '../../playing/openingLocalize';
import { formatDateTime } from '../../utils/regionalFormat';
import {
  formatDeadlineCountdown,
  deadlineUrgency,
  formatRatingChange,
  formatResultReason,
  formatTimeControl,
  materialSnapshot,
  MoveList,
  PlayerClockBar,
  PlayingBanner,
  PlayingBoardSkeleton,
  PlayingStatusChip,
} from './playingUi';
import PlayingConfirmDialog from './PlayingConfirmDialog';
import PlayingGameControls from './PlayingGameControls';
import PlayingBoardTools, { PlayingGameOptions, PlayingRematchActions } from './PlayingBoardTools';
import { PlayingDisclosure } from './PlayingDisclosure';
import { apiErrorMessage } from '../../utils/apiError';

function fenSide(fen) {
  return (fen || '').split(' ')[1] || 'w';
}

const BOARD_THEMES = {
  classic: { dark: '#769656', light: '#eeeed2' },
  green: { dark: '#4a7c59', light: '#e8f0e0' },
  brown: { dark: '#b58863', light: '#f0d9b5' },
  blue: { dark: '#4b6b8a', light: '#d6e0ea' },
  highContrast: { dark: '#111111', light: '#f5f5f5' },
};

function boardColors(theme, highContrast) {
  if (highContrast) return BOARD_THEMES.highContrast;
  return BOARD_THEMES[theme] || BOARD_THEMES.classic;
}

const UNICODE_PIECES = {
  wK: '♔', wQ: '♕', wR: '♖', wB: '♗', wN: '♘', wP: '♙',
  bK: '♚', bQ: '♛', bR: '♜', bB: '♝', bN: '♞', bP: '♟',
};

function unicodePieceRenderer({ squareWidth, piece }) {
  return (
    <div
      style={{
        width: squareWidth,
        height: squareWidth,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: squareWidth * 0.82,
        lineHeight: 1,
        userSelect: 'none',
      }}
    >
      {UNICODE_PIECES[piece] || ''}
    </div>
  );
}

function customPiecesForSet(pieceSet) {
  if (pieceSet === 'unicode' || pieceSet === 'symbol') {
    const out = {};
    Object.keys(UNICODE_PIECES).forEach((k) => {
      out[k] = unicodePieceRenderer;
    });
    return out;
  }
  return undefined;
}

function animDurationMs(speed, reducedMotion = false) {
  if (reducedMotion) return 0;
  if (speed === 'slow') return 320;
  if (speed === 'fast') return 120;
  return 220;
}

/** True when formatClock would show the same text (avoids 250ms board re-renders). */
function clockDisplayEqual(a, b) {
  if (a == null && b == null) return true;
  if (a == null || b == null) return false;
  const aq = a < 10000 ? Math.floor(a / 100) : Math.floor(a / 1000);
  const bq = b < 10000 ? Math.floor(b / 100) : Math.floor(b / 1000);
  return aq === bq;
}

function needsPromotion(chess, from, to) {
  const piece = chess.get(from);
  if (!piece || piece.type !== 'p') return false;
  const rank = to?.[1];
  return (piece.color === 'w' && rank === '8') || (piece.color === 'b' && rank === '1');
}

function pvUciToSan(fen, pvTokens = []) {
  try {
    const c = new Chess(fen);
    const sans = [];
    for (const uci of pvTokens) {
      if (!uci || uci.length < 4) break;
      const from = uci.slice(0, 2);
      const to = uci.slice(2, 4);
      const promotion = uci[4];
      const mv = c.move({ from, to, promotion });
      if (!mv) break;
      sans.push(mv.san);
    }
    return sans;
  } catch {
    return pvTokens;
  }
}

function legalOptionSquares(chess, square) {
  const moves = chess.moves({ square, verbose: true });
  if (!moves.length) return null;
  const next = {};
  moves.forEach((m) => {
    next[m.to] = {
      background: m.captured
        ? 'radial-gradient(circle, rgba(0,0,0,.15) 85%, transparent 86%)'
        : 'radial-gradient(circle, rgba(0,0,0,.18) 22%, transparent 23%)',
    };
  });
  next[square] = { backgroundColor: 'rgba(59, 130, 246, 0.35)' };
  return next;
}

function pickFallbackMove(chess, level = 3) {
  const moves = chess.moves({ verbose: true });
  if (!moves.length) return null;
  const captures = moves.filter((m) => m.captured);
  const pool = level >= 4 && captures.length ? captures : moves;
  return pool[Math.floor(Math.random() * pool.length)];
}

function buildSquareStyles(chess, optionSquares) {
  const styles = { ...optionSquares };
  const last = chess.history({ verbose: true }).slice(-1)[0];
  if (last) {
    styles[last.from] = {
      ...(styles[last.from] || {}),
      backgroundColor: 'rgba(250, 204, 21, 0.45)',
    };
    styles[last.to] = {
      ...(styles[last.to] || {}),
      backgroundColor: 'rgba(250, 204, 21, 0.65)',
    };
  }
  if (chess.isCheck()) {
    const turn = chess.turn();
    const board = chess.board();
    for (let r = 0; r < 8; r++) {
      for (let c = 0; c < 8; c++) {
        const p = board[r][c];
        if (p && p.type === 'k' && p.color === turn) {
          const sq = `${'abcdefgh'[c]}${8 - r}`;
          styles[sq] = {
            ...(styles[sq] || {}),
            backgroundColor: 'rgba(239, 68, 68, 0.55)',
          };
        }
      }
    }
  }
  return styles;
}

export default function PlayGamePage({ spectateOnly = false }) {
  const { t } = useTranslation();
  const { gameId } = useParams();
  const [search, setSearch] = useSearchParams();
  const navigate = useNavigate();
  const [gameMeta, setGameMeta] = useState(null);
  const [chess, setChess] = useState(() => new Chess());
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [whiteMs, setWhiteMs] = useState(null);
  const [blackMs, setBlackMs] = useState(null);
  const [moveFrom, setMoveFrom] = useState(null);
  const [optionSquares, setOptionSquares] = useState({});
  const [flipOverride, setFlipOverride] = useState(null);
  const [copiedLink, setCopiedLink] = useState(false);
  const [boardWidth, setBoardWidth] = useState(480);
  const [confirmResign, setConfirmResign] = useState(false);
  const [confirmAbort, setConfirmAbort] = useState(false);
  const [shortcutHelpOpen, setShortcutHelpOpen] = useState(false);
  const [pendingMoveConfirm, setPendingMoveConfirm] = useState(null); // { from, to }
  const [engineThinking, setEngineThinking] = useState(false);
  const [pendingPromotion, setPendingPromotion] = useState(null);
  const [viewPly, setViewPly] = useState(null); // null = live tip
  const [soundMuted, setSoundMuted] = useState(() => isPlayingMuted());
  const [boardTheme, setBoardTheme] = useState(() => {
    try {
      return localStorage.getItem('playing.boardTheme') || 'classic';
    } catch {
      return 'classic';
    }
  });
  const [pieceSet, setPieceSet] = useState('cburnett');
  const [moveConfirmation, setMoveConfirmation] = useState(() => {
    try {
      return localStorage.getItem('playing.moveConfirmation') === 'true';
    } catch {
      return false;
    }
  });
  const [animationSpeed, setAnimationSpeed] = useState('normal');
  const reducedMotion = usePrefersReducedMotion();
  const [highContrast, setHighContrast] = useState(() => {
    try {
      return localStorage.getItem('playing.highContrast') === 'true';
    } catch {
      return false;
    }
  });
  const [autoQueen, setAutoQueen] = useState(false);
  const [showBookHints, setShowBookHints] = useState(() => {
    try {
      const v = localStorage.getItem('playing.showBookHints');
      // Default OFF — opening arrows confuse players who did not ask for hints.
      if (v === null) return false;
      return v === 'true';
    } catch {
      return false;
    }
  });
  const [resultDismissed, setResultDismissed] = useState(false);
  const [reportBusy, setReportBusy] = useState(false);
  const [reportNotice, setReportNotice] = useState('');
  const [reportOpen, setReportOpen] = useState(false);
  const [reportReason, setReportReason] = useState('SUSPECTED_ASSISTANCE');
  const [reportDetails, setReportDetails] = useState('');
  const [wsStatus, setWsStatus] = useState('connecting');
  const [analysisOn, setAnalysisOn] = useState(false);
  const [analysisBusy, setAnalysisBusy] = useState(false);
  const [analysisProgress, setAnalysisProgress] = useState(0);
  const [analysisByPly, setAnalysisByPly] = useState({});
  const [analysisSummary, setAnalysisSummary] = useState(null);
  const [analysisPreset, setAnalysisPreset] = useState('deep');
  const [qualitiesByPly, setQualitiesByPly] = useState({});
  const [clubReviewHint, setClubReviewHint] = useState('');
  const [clubReviewBusy, setClubReviewBusy] = useState(false);
  const [rematchSent, setRematchSent] = useState(false);
  const [rematchBusy, setRematchBusy] = useState(false);
  const [pendingRematchChallengeId, setPendingRematchChallengeId] = useState(null);
  const [incomingRematchChallenge, setIncomingRematchChallenge] = useState(null);
  const [rematchInboxError, setRematchInboxError] = useState('');
  const [challengeDeclinedNotice, setChallengeDeclinedNotice] = useState('');
  const [deadlineNow, setDeadlineNow] = useState(() => Date.now());
  const [firstMoveNow, setFirstMoveNow] = useState(() => Date.now());
  const [moveText, setMoveText] = useState('');
  const [chatMessages, setChatMessages] = useState([]);
  const [chatDraft, setChatDraft] = useState('');
  const [chatBusy, setChatBusy] = useState(false);
  const [chatError, setChatError] = useState('');
  /** Progressive disclosure: chat / options / typed moves stay closed until requested. */
  const [chatOpen, setChatOpen] = useState(false);
  const [optionsOpen, setOptionsOpen] = useState(false);
  const [typeMoveOpen, setTypeMoveOpen] = useState(false);
  const chatEndRef = useRef(null);
  const chatReloadRef = useRef(null);
  const rematchInboxReloadRef = useRef(null);
  const boardWrapRef = useRef(null);
  const engineRef = useRef(null);
  const analysisAbortRef = useRef(0);
  const deepLinkAppliedRef = useRef(false);
  const plyUrlReadyRef = useRef(false);
  const [engineReady, setEngineReady] = useState(false);
  const computerBusyRef = useRef(false);
  const computerGenRef = useRef(0);
  const claimingTimeoutRef = useRef(false);
  const prevPlyRef = useRef(0);
  const prevFinishedRef = useRef(false);
  const prevOfferRef = useRef({ draw: false, takeback: false });
  const lastPosKeyRef = useRef('');
  const gameMetaRef = useRef(null);
  const [premove, setPremove] = useState(null);
  const [sharedAnalysis, setSharedAnalysis] = useState(null);
  const [shareNotice, setShareNotice] = useState('');
  const premovePendingRef = useRef(false);
  /** Challenger leaving a PENDING waiting board must cancel the challenge. */
  const pendingLeaveRef = useRef({ gameId: null, isSender: false, aborting: false });
  const waitingMetaRef = useRef({ status: null, from: null, id: null });
  const clockSnapshotRef = useRef({ whiteMs: null, blackMs: null, lastMoveAt: null, side: 'w' });
  const lowTimeTickRef = useRef(false);

  const baseOrientation = spectateOnly
    ? 'white'
    : (gameMeta?.myColor === 'b' ? 'black' : 'white');
  const orientation = flipOverride || baseOrientation;

  const finished = useMemo(() => {
    const s = gameMeta?.status;
    return s && s !== 'ACTIVE' && s !== 'PENDING';
  }, [gameMeta]);

  const sideToMove = fenSide(chess.fen());
  const turnLabel = sideToMove === 'w' ? t('playing.whiteToMove') : t('playing.blackToMove');
  const myTurn = !spectateOnly && !finished && gameMeta?.myColor === sideToMove;
  const inCheck = !finished && typeof chess.inCheck === 'function' && chess.inCheck();

  const applyMeta = useCallback((g) => {
    const { game: merged, needsFullSync } = mergeGameUpdate(gameMetaRef.current, g);
    const viewed = withViewerPerspective(merged, getUserId());
    gameMetaRef.current = viewed;
    setGameMeta(viewed);
    const { whiteMs: w, blackMs: b } = clocksAdjustedFromMeta(viewed);
    setWhiteMs(w);
    setBlackMs(b);
    const c = chessFromGamePayload(Chess, viewed);
    const moveCount = Array.isArray(viewed.moves) ? viewed.moves.length : (viewed.moveCount ?? c.history().length);
    const posKey = `${viewed.id}|${viewed.status}|${boardPosKey(c.fen(), moveCount)}`;
    const posChanged = lastPosKeyRef.current !== posKey;
    // Only remount the board when the position actually changed. REST confirm + STOMP
    // echo of our own move used to call setChess twice and re-trigger piece animations.
    if (posChanged) {
      lastPosKeyRef.current = posKey;
      setChess(c);
      setMoveFrom(null);
      setOptionSquares({});
      setPendingPromotion(null);
      setConfirmResign(false);
      setConfirmAbort(false);
      setViewPly(null);
      // Keep premove until turn-fire effect validates/clears it
    }
    if (needsFullSync && viewed?.id) {
      // Takeback / rewind: slim payload cannot rebuild history — pull full game once
      const loader = spectateOnly ? spectateGame(viewed.id) : getChessGame(viewed.id);
      loader
        .then((full) => {
          if (!full) return;
          const again = withViewerPerspective(full, getUserId());
          gameMetaRef.current = again;
          setGameMeta(again);
          const clocks = clocksAdjustedFromMeta(again);
          setWhiteMs(clocks.whiteMs);
          setBlackMs(clocks.blackMs);
          const rebuilt = chessFromGamePayload(Chess, again);
          const mc = Array.isArray(again.moves) ? again.moves.length : rebuilt.history().length;
          const key = `${again.id}|${again.status}|${boardPosKey(rebuilt.fen(), mc)}`;
          if (lastPosKeyRef.current !== key) {
            lastPosKeyRef.current = key;
            setChess(rebuilt);
          }
        })
        .catch(() => {});
    }
  }, [spectateOnly]);

  const syncFromServer = useCallback(async (id) => {
    const g = spectateOnly ? await spectateGame(id) : await getChessGame(id);
    applyMeta(g);
    return g;
  }, [applyMeta, spectateOnly]);

  const syncStateFromServer = useCallback(async (id) => {
    try {
      const g = await getChessGameState(id);
      applyMeta(g);
      return g;
    } catch (e) {
      // Fall back to full game if state endpoint unavailable
      return syncFromServer(id);
    }
  }, [applyMeta, syncFromServer]);

  useEffect(() => {
    if (!confirmResign) return undefined;
    const tmr = setTimeout(() => setConfirmResign(false), 8000);
    return () => clearTimeout(tmr);
  }, [confirmResign]);

  useEffect(() => {
    if (!confirmAbort) return undefined;
    const tmr = setTimeout(() => setConfirmAbort(false), 8000);
    return () => clearTimeout(tmr);
  }, [confirmAbort]);

  useEffect(() => {
    let cancelled = false;
    getPlayingPrefs()
      .then((p) => {
        if (cancelled || !p) return;
        if (p.boardTheme) setBoardTheme(p.boardTheme);
        if (p.pieceSet) setPieceSet(p.pieceSet);
        if (typeof p.moveConfirmation === 'boolean') setMoveConfirmation(p.moveConfirmation);
        if (p.animationSpeed) setAnimationSpeed(p.animationSpeed);
        if (typeof p.highContrast === 'boolean') setHighContrast(p.highContrast);
        if (typeof p.autoQueen === 'boolean') setAutoQueen(p.autoQueen);
        if (typeof p.showBookHints === 'boolean') setShowBookHints(p.showBookHints);
        if (typeof p.soundEnabled === 'boolean') {
          const muted = !p.soundEnabled;
          setSoundMuted(muted);
          setPlayingMuted(muted);
        }
        try {
          if (p.boardTheme) localStorage.setItem('playing.boardTheme', p.boardTheme);
          if (typeof p.moveConfirmation === 'boolean') {
            localStorage.setItem('playing.moveConfirmation', String(p.moveConfirmation));
          }
          if (typeof p.highContrast === 'boolean') {
            localStorage.setItem('playing.highContrast', String(p.highContrast));
          }
          if (typeof p.showBookHints === 'boolean') {
            localStorage.setItem('playing.showBookHints', String(p.showBookHints));
          }
        } catch {
          /* ignore */
        }
      })
      .catch(() => {
        /* localStorage fallback already applied */
      });
    return () => { cancelled = true; };
  }, []);

  const persistPrefs = useCallback((patch) => {
    try {
      if (patch.boardTheme != null) localStorage.setItem('playing.boardTheme', patch.boardTheme);
      if (patch.moveConfirmation != null) {
        localStorage.setItem('playing.moveConfirmation', String(patch.moveConfirmation));
      }
      if (patch.highContrast != null) localStorage.setItem('playing.highContrast', String(patch.highContrast));
      if (patch.showBookHints != null) localStorage.setItem('playing.showBookHints', String(patch.showBookHints));
    } catch {
      /* ignore */
    }
    return updatePlayingPrefs(patch).catch(() => {
      setError(t('playing.prefsSaveFailed', {
        defaultValue: 'Could not sync preferences to the server.',
      }));
      return null;
    });
  }, [t]);

  useEffect(() => {
    engineRef.current = createStockfishWorker();
    setEngineReady(true);
    return () => {
      setEngineReady(false);
      try {
        engineRef.current?.dispose();
      } catch {
        /* ignore */
      }
      engineRef.current = null;
    };
  }, []);

  useEffect(() => {
    warmOpeningBook();
  }, []);

  useEffect(() => {
    const el = boardWrapRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect?.width;
      if (!w) return;
      const next = Math.max(240, Math.floor(w));
      // Ignore 1–3px jitter from clock text / layout so pieces are not remounted every tick.
      setBoardWidth((prev) => (Math.abs(prev - next) < 4 ? prev : next));
    });
    ro.observe(el);
    setBoardWidth(Math.max(240, Math.floor(el.clientWidth || 480)));
    return () => ro.disconnect();
  }, [loading, gameMeta?.id]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError('');
        if (gameId) {
          await syncFromServer(gameId);
        } else if (!spectateOnly) {
          const colorPref = String(search.get('color') || 'white').toLowerCase();
          const playAsWhite = colorPref === 'random'
            ? Math.random() < 0.5
            : colorPref !== 'black';
          const level = Number(search.get('level') || 3);
          const rated = search.get('rated') === '1';
          const tc = Number(search.get('tc') || 600);
          const inc = Number(search.get('inc') || 0);
          const g = await startComputerGame({
            playAsWhite,
            computerLevel: level,
            rated,
            timeControlSeconds: tc,
            incrementSeconds: inc,
          });
          if (!cancelled) navigate(`/playing/game/${g.id}`, { replace: true });
        }
      } catch (e) {
        if (!cancelled) setError(apiErrorMessage(e, t('playing.loadFailed')));
      } finally {
        if (!cancelled) {
          setBusy(false);
          setLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [gameId, spectateOnly]);

  useEffect(() => {
    if (!gameMeta?.id) return undefined;
    setWsStatus('connecting');
    return subscribeChessGame(gameMeta.id, (g) => {
      const prev = waitingMetaRef.current;
      const me = getUserId();
      const wasSenderWaiting = prev.status === 'PENDING'
        && me != null
        && prev.from != null
        && Number(me) === Number(prev.from);
      applyMeta(g);
      if (
        wasSenderWaiting
        && g?.status === 'ABORTED'
        && !pendingLeaveRef.current.aborting
      ) {
        setChallengeDeclinedNotice(t('playing.challengeDeclined'));
        pendingLeaveRef.current = { gameId: null, isSender: false, aborting: false };
        window.setTimeout(() => navigate('/playing'), 1400);
      }
    }, setWsStatus);
  }, [gameMeta?.id, applyMeta, navigate, t]);

  // Keep leave-ref in sync so unmount/Back hub can cancel orphan PENDING challenges
  useEffect(() => {
    const me = getUserId();
    const isSender = gameMeta?.status === 'PENDING'
      && !spectateOnly
      && me != null
      && gameMeta?.challengeFromUserId != null
      && Number(me) === Number(gameMeta.challengeFromUserId);
    waitingMetaRef.current = {
      status: gameMeta?.status,
      from: gameMeta?.challengeFromUserId,
      id: gameMeta?.id,
    };
    pendingLeaveRef.current.gameId = isSender ? gameMeta?.id : null;
    pendingLeaveRef.current.isSender = !!isSender;
  }, [gameMeta?.id, gameMeta?.status, gameMeta?.challengeFromUserId, spectateOnly]);

  useEffect(() => () => {
    const leave = pendingLeaveRef.current;
    if (!leave.isSender || !leave.gameId || leave.aborting) return;
    leave.aborting = true;
    abortChessGame(leave.gameId).catch(() => {
      try {
        sessionStorage.setItem('playing.abortLeaveFailed', '1');
      } catch {
        /* ignore */
      }
    });
  }, []);

  // Tab close / refresh while challenger waits — best-effort abort (browser may truncate the request)
  useEffect(() => {
    const onBeforeUnload = () => {
      const leave = pendingLeaveRef.current;
      if (!leave.isSender || !leave.gameId || leave.aborting) return;
      leave.aborting = true;
      const url = `/api/playing/games/${leave.gameId}/abort`;
      try {
        const token = getToken();
        fetch(url, {
          method: 'POST',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          keepalive: true,
          credentials: 'same-origin',
        }).catch(() => {});
      } catch {
        /* ignore */
      }
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, []);

  // Rematch / outgoing challenge updates: clear rematchSent on decline; keep id for cancel
  // Also capture incoming rematch for this finished game.
  useEffect(() => {
    const uid = getUserId();
    if (uid == null) return undefined;
    return subscribeIncomingChallenges(uid, (c) => {
      if (!c?.id) return;
      const status = String(c.status || '').toUpperCase();
      const rematchOf = c.rematchFromGameId != null ? Number(c.rematchFromGameId) : null;
      if (
        rematchOf != null
        && gameMeta?.id != null
        && rematchOf === Number(gameMeta.id)
        && Number(c.toUserId) === Number(uid)
      ) {
        if (status === 'PENDING') {
          setIncomingRematchChallenge(c);
          setResultDismissed(false);
        } else {
          setIncomingRematchChallenge((cur) => (cur && Number(cur.id) === Number(c.id) ? null : cur));
        }
      }
      if (pendingRematchChallengeId != null && Number(c.id) === Number(pendingRematchChallengeId)) {
        if (status === 'DECLINED' || status === 'CANCELLED') {
          setPendingRematchChallengeId(null);
          setRematchSent(false);
          if (status === 'DECLINED') {
            setChallengeDeclinedNotice(t('playing.challengeDeclined'));
          }
        } else if (status === 'ACCEPTED' && c.gameId) {
          setPendingRematchChallengeId(null);
          setRematchSent(false);
          navigate(`/playing/game/${c.gameId}`);
        }
      }
    });
  }, [pendingRematchChallengeId, navigate, t, gameMeta?.id]);

  // Seed incoming rematch if opponent already challenged while we were offline
  useEffect(() => {
    const isHuman = gameMeta?.gameType && gameMeta.gameType !== 'VS_COMPUTER';
    if (!finished || !isHuman || spectateOnly || !gameMeta?.id) {
      setIncomingRematchChallenge(null);
      setRematchInboxError('');
      return undefined;
    }
    let cancelled = false;
    const loadRematchInbox = () => {
      setRematchInboxError('');
      getIncomingChallenges()
        .then((rows) => {
          if (cancelled) return;
          const hit = (Array.isArray(rows) ? rows : []).find(
            (c) => Number(c.rematchFromGameId) === Number(gameMeta.id)
              && String(c.status || '').toUpperCase() === 'PENDING',
          );
          setIncomingRematchChallenge(hit || null);
        })
        .catch(() => {
          if (!cancelled) {
            setRematchInboxError(t('playing.rematchInboxLoadFailed', {
              defaultValue: 'Could not check for rematch offers.',
            }));
          }
        });
    };
    loadRematchInbox();
    rematchInboxReloadRef.current = loadRematchInbox;
    return () => {
      cancelled = true;
      rematchInboxReloadRef.current = null;
    };
  }, [finished, spectateOnly, gameMeta?.id, gameMeta?.gameType, t]);

  useEffect(() => {
    if (!gameMeta?.id) return undefined;
    let cancelled = false;
    getSharedChessAnalysis(gameMeta.id)
      .then((payload) => {
        if (cancelled || !payload) return;
        if (String(payload.sharedBy) === String(getUserId())) return;
        setSharedAnalysis(payload);
      })
      .catch(() => { /* optional */ });
    return () => { cancelled = true; };
  }, [gameMeta?.id]);

  useEffect(() => {
    if (!gameMeta?.id) return undefined;
    return subscribeChessGameAnalysis(gameMeta.id, (payload) => {
      if (!payload || String(payload.sharedBy) === String(getUserId())) return;
      setSharedAnalysis(payload);
    });
  }, [gameMeta?.id]);

  useEffect(() => {
    setSharedAnalysis(null);
    setPremove(null);
    premovePendingRef.current = false;
  }, [gameMeta?.id]);

  useEffect(() => {
    if (!gameMeta?.id || gameMeta.gameType === 'VS_COMPUTER') {
      setChatMessages([]);
      return undefined;
    }
    let cancelled = false;
    const loadChat = () => {
      setChatError('');
      getChessChat(gameMeta.id)
        .then((rows) => {
          if (!cancelled) setChatMessages(Array.isArray(rows) ? rows : []);
        })
        .catch(() => {
          if (!cancelled) setChatError(t('playing.chatLoadFailed'));
        });
    };
    loadChat();
    const unsub = subscribeChessGameChat(gameMeta.id, (msg) => {
      if (!msg?.id) return;
      const mine = msg.userId != null && String(msg.userId) === String(getUserId());
      setChatMessages((prev) => {
        if (prev.some((m) => m.id === msg.id)) return prev;
        return [...prev, { ...msg, mine: msg.mine || mine }];
      });
      // Open chat when the opponent speaks — otherwise stay collapsed.
      if (!(msg.mine || mine)) setChatOpen(true);
    });
    // Expose retry via ref for the chat panel button
    chatReloadRef.current = loadChat;
    return () => {
      cancelled = true;
      unsub?.();
      chatReloadRef.current = null;
    };
  }, [gameMeta?.id, gameMeta?.gameType, t]);

  useEffect(() => {
    if (wsStatus !== 'disconnected' || !gameMeta?.id || gameMeta.gameType === 'VS_COMPUTER') return undefined;
    const iv = setInterval(() => {
      chatReloadRef.current?.();
    }, 8000);
    return () => clearInterval(iv);
  }, [wsStatus, gameMeta?.id, gameMeta?.gameType]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' });
  }, [chatMessages.length]);

  useEffect(() => {
    if (wsStatus !== 'disconnected' || !gameMeta?.id || finished) return undefined;
    let delay = 4000;
    let timer = null;
    let cancelled = false;
    const tick = () => {
      syncStateFromServer(gameMeta.id)
        .catch(() => {})
        .finally(() => {
          if (cancelled) return;
          delay = Math.min(delay * 1.5, 20000);
          timer = setTimeout(tick, delay + Math.floor(Math.random() * 500));
        });
    };
    timer = setTimeout(tick, delay);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [wsStatus, gameMeta?.id, finished, syncStateFromServer]);

  useEffect(() => {
    if (!gameMeta?.id || finished) return undefined;
    const resync = () => {
      if (document.visibilityState === 'visible') {
        syncFromServer(gameMeta.id).catch(() => {});
      }
    };
    document.addEventListener('visibilitychange', resync);
    window.addEventListener('focus', resync);
    return () => {
      document.removeEventListener('visibilitychange', resync);
      window.removeEventListener('focus', resync);
    };
  }, [gameMeta?.id, finished, syncFromServer]);

  useEffect(() => {
    if (finished || !gameMeta) return undefined;
    // Challenge waiting room / not started — freeze clocks
    if (gameMeta.status === 'PENDING' || !gameMeta.lastMoveAt) return undefined;
    if (gameMeta.daysPerMove != null && Number(gameMeta.daysPerMove) > 0) return undefined;
    // Brand-new game (no plies yet): keep full time on both sides until ply 1.
    const plyCount = Array.isArray(gameMeta.moves)
      ? gameMeta.moves.length
      : (gameMeta.moveCount ?? chess.history().length);
    if (Number(plyCount) <= 0) return undefined;

    const side = fenSide(chess.fen());
    const baseWhite = gameMeta.whiteTimeMs;
    const baseBlack = gameMeta.blackTimeMs;
    const last = parseApiDateMs(gameMeta.lastMoveAt);
    if (!Number.isFinite(last) || baseWhite == null || baseBlack == null) return undefined;

    clockSnapshotRef.current = {
      whiteMs: baseWhite,
      blackMs: baseBlack,
      lastMoveAt: last,
      side,
    };
    lowTimeTickRef.current = false;

    const tick = () => {
      const snap = clockSnapshotRef.current;
      const elapsed = Math.max(0, Date.now() - snap.lastMoveAt);
      let w = snap.whiteMs;
      let b = snap.blackMs;
      if (snap.side === 'w') w = Math.max(0, snap.whiteMs - elapsed);
      else b = Math.max(0, snap.blackMs - elapsed);
      // Only commit React state when the displayed clock string would change —
      // 250ms setState was re-rendering Chessboard and causing piece flicker.
      setWhiteMs((prev) => (clockDisplayEqual(prev, w) ? prev : w));
      setBlackMs((prev) => (clockDisplayEqual(prev, b) ? prev : b));
      const myColor = gameMeta.myColor;
      const myMs = myColor === 'b' ? b : w;
      const myActive = (myColor === 'w' && snap.side === 'w') || (myColor === 'b' && snap.side === 'b');
      if (myActive && myMs != null && myMs < 10000 && myMs > 0 && !lowTimeTickRef.current) {
        lowTimeTickRef.current = true;
        playChessCue('tick');
      }
      if (myMs == null || myMs >= 10000) lowTimeTickRef.current = false;
    };
    tick();
    const iv = setInterval(tick, 250);
    return () => clearInterval(iv);
  }, [chess.fen(), finished, gameMeta?.id, gameMeta?.status, gameMeta?.lastMoveAt, gameMeta?.daysPerMove, gameMeta?.whiteTimeMs, gameMeta?.blackTimeMs, gameMeta?.myColor, gameMeta?.moveCount, gameMeta?.moves]);

  useEffect(() => {
    claimingTimeoutRef.current = false;
  }, [gameMeta?.id]);

  useEffect(() => {
    if (!gameMeta?.moveDeadlineAt || finished) return undefined;
    setDeadlineNow(Date.now());
    const id = window.setInterval(() => setDeadlineNow(Date.now()), 15_000);
    return () => window.clearInterval(id);
  }, [gameMeta?.moveDeadlineAt, finished]);

  useEffect(() => {
    if (!gameMeta?.firstMoveDeadlineAt || finished) return undefined;
    setFirstMoveNow(Date.now());
    const id = window.setInterval(() => setFirstMoveNow(Date.now()), 250);
    return () => window.clearInterval(id);
  }, [gameMeta?.firstMoveDeadlineAt, finished]);

  useEffect(() => {
    if (finished || spectateOnly || !gameMeta?.id) return undefined;
    if (gameMeta.status === 'PENDING') return undefined;

    const isCorr = gameMeta.daysPerMove != null && Number(gameMeta.daysPerMove) > 0;
    let cancelled = false;
    let timerId = null;

    const runClaim = () => {
      if (cancelled || claimingTimeoutRef.current) return;
      claimingTimeoutRef.current = true;
      claimTimeout(gameMeta.id)
        .then((g) => {
          if (cancelled) return;
          applyMeta(g);
          setError('');
        })
        .catch(() => {
          if (cancelled) return;
          syncFromServer(gameMeta.id).catch(() => setError(t('playing.timeoutFailed')));
        })
        .finally(() => {
          window.setTimeout(() => {
            if (!cancelled) claimingTimeoutRef.current = false;
          }, 2500);
        });
    };

    // Live first-move buffer: abort if White never opened in time.
    const firstDeadline = gameMeta.firstMoveDeadlineAt
      ? parseApiDateMs(gameMeta.firstMoveDeadlineAt)
      : NaN;
    if (!isCorr && Number.isFinite(firstDeadline)) {
      const delay = firstDeadline - Date.now();
      if (delay <= 0) {
        runClaim();
      } else {
        timerId = window.setTimeout(runClaim, delay + 50);
      }
      return () => {
        cancelled = true;
        if (timerId != null) window.clearTimeout(timerId);
      };
    }

    if (isCorr) {
      const deadline = gameMeta.moveDeadlineAt ? Date.parse(gameMeta.moveDeadlineAt) : NaN;
      if (!Number.isFinite(deadline)) return undefined;
      const delay = deadline - Date.now();
      if (delay <= 0) {
        runClaim();
      } else {
        // Cap setTimeout to avoid 32-bit overflow on long correspondence windows
        const capped = Math.min(delay, 2_147_000_000);
        timerId = window.setTimeout(() => {
          if (cancelled) return;
          if (Date.now() >= deadline) runClaim();
          else {
            // Reschedule remainder if we hit the cap
            timerId = window.setTimeout(runClaim, Math.max(0, deadline - Date.now()));
          }
        }, capped);
      }
      return () => {
        cancelled = true;
        if (timerId != null) window.clearTimeout(timerId);
      };
    }

    if ((whiteMs == null || whiteMs > 0) && (blackMs == null || blackMs > 0)) {
      return undefined;
    }
    runClaim();
    return () => {
      cancelled = true;
    };
  }, [whiteMs, blackMs, finished, spectateOnly, gameMeta?.id, gameMeta?.status, gameMeta?.daysPerMove, gameMeta?.moveDeadlineAt, gameMeta?.firstMoveDeadlineAt, applyMeta, syncFromServer, t]);

  const runComputer = useCallback(async (localChess, meta) => {
    if (!meta || meta.gameType !== 'VS_COMPUTER' || finished || spectateOnly) return;
    if (computerBusyRef.current) return;
    const side = fenSide(localChess.fen());
    const computerIsWhite = !meta.whiteUserId;
    const computerToMove = (computerIsWhite && side === 'w') || (!computerIsWhite && side === 'b');
    if (!computerToMove) return;

    const gen = computerGenRef.current + 1;
    computerGenRef.current = gen;
    const fenAtStart = localChess.fen();
    computerBusyRef.current = true;
    setEngineThinking(true);
    try {
      let uci = null;
      try {
        const hist = localChess.history({ verbose: true }).map((m) => `${m.from}${m.to}${m.promotion || ''}`);
        const book = bookReply(hist);
        if (book) uci = book;
      } catch {
        /* ignore */
      }
      if (!uci) {
        try {
          const clockMs = computerIsWhite
            ? (meta.whiteTimeMs ?? meta.whiteClockMs)
            : (meta.blackTimeMs ?? meta.blackClockMs);
          uci = await engineRef.current?.getMoveForLevel(localChess.fen(), meta.computerLevel || 3, {
            clockMs: clockMs != null ? Number(clockMs) : undefined,
          });
        } catch {
          uci = null;
        }
      }
      if (computerGenRef.current !== gen) return;
      const engineBoard = new Chess(fenAtStart);
      let move;
      if (uci && uci.length >= 4) {
        try {
          move = engineBoard.move({
            from: uci.slice(0, 2),
            to: uci.slice(2, 4),
            promotion: uci.length > 4 ? uci[4] : undefined,
          });
        } catch {
          move = null;
        }
      }
      if (!move) {
        const fb = pickFallbackMove(engineBoard, meta.computerLevel || 3);
        if (!fb) return;
        move = engineBoard.move(fb);
        uci = `${move.from}${move.to}${move.promotion || ''}`;
      }
      if (computerGenRef.current !== gen) return;
      const updated = await postChessMove(meta.id, {
        san: move.san,
        uci,
        fenAfter: engineBoard.fen(),
        pgn: engineBoard.pgn(),
      });
      if (computerGenRef.current !== gen) return;
      applyMeta(updated);
    } catch (e) {
      if (computerGenRef.current === gen) {
        setError(apiErrorMessage(e, t('playing.engineFailed')));
      }
    } finally {
      if (computerGenRef.current === gen) {
        computerBusyRef.current = false;
        setEngineThinking(false);
      }
    }
  }, [finished, spectateOnly, applyMeta, t]);

  useEffect(() => {
    if (!gameMeta || busy || spectateOnly || loading || finished) return undefined;
    if (gameMeta.status && gameMeta.status !== 'ACTIVE' && gameMeta.status !== 'PENDING') return undefined;
    const tmr = setTimeout(() => {
      runComputer(chess, gameMeta).catch(() => setError(t('playing.engineFailed')));
    }, 300);
    return () => clearTimeout(tmr);
  }, [gameMeta, chess, finished, busy, spectateOnly, loading, runComputer, t]);

  const fullHistory = useMemo(() => chess.history(), [chess]);
  const fullVerbose = useMemo(() => chess.history({ verbose: true }), [chess]);
  const canScrub = !!gameMeta;
  const activePly = viewPly == null ? fullHistory.length : viewPly;
  const viewingPast = viewPly != null && viewPly < fullHistory.length;
  const { setGameContext, clearGameContext } = useChessCoach();

  useEffect(() => {
    if (!gameMeta?.id) {
      clearGameContext();
      return undefined;
    }
    setGameContext({
      gameId: gameMeta.id,
      focusPly: activePly > 0 ? activePly : null,
    });
    return () => clearGameContext();
  }, [gameMeta?.id, activePly, setGameContext, clearGameContext]);
  const openingInfo = useMemo(() => {
    const ucis = fullVerbose.map((m) => `${m.from}${m.to}${m.promotion || ''}`);
    return lookupOpening(ucis);
  }, [fullVerbose]);
  const bookHintCandidates = useMemo(() => {
    // Opening-book arrows are a computer-play training aid only — never in live human games.
    if (!showBookHints || analysisOn || finished || spectateOnly) return [];
    if (String(gameMeta?.gameType || '').toUpperCase() !== 'VS_COMPUTER') return [];
    const ucis = fullVerbose
      .slice(0, activePly)
      .map((m) => `${m.from}${m.to}${m.promotion || ''}`);
    return bookCandidates(ucis).slice(0, 3);
  }, [showBookHints, analysisOn, finished, spectateOnly, gameMeta?.gameType, fullVerbose, activePly]);

  // Club review: hydrate from server anti-cheat fields; honest stop for SKIPPED / empty / FAILED.
  useEffect(() => {
    if (!finished || !gameMeta?.id) {
      setClubReviewHint('');
      return undefined;
    }
    if (String(gameMeta.gameType || '').toUpperCase() === 'VS_COMPUTER') {
      setClubReviewHint('');
      return undefined;
    }
    const source = analysisSummary?.source;
    if (source === 'local' || source === 'cloud' || source === 'club') {
      setClubReviewHint('');
      return undefined;
    }

    const applyClub = (moves) => {
      const review = serverMovesToClubReview(moves);
      if (!review) return false;
      setAnalysisOn(true);
      setQualitiesByPly(review.qualities);
      setAnalysisByPly(review.byPly);
      setAnalysisSummary(review.summary);
      setClubReviewHint('');
      return true;
    };

    if (applyClub(gameMeta.moves)) return undefined;

    const moveCount = gameMeta.moveCount != null
      ? Number(gameMeta.moveCount)
      : (Array.isArray(gameMeta.moves) ? gameMeta.moves.length : fullHistory.length);
    const status = String(gameMeta.clubReviewStatus || '').toUpperCase();

    if (gameMeta.clubReviewReady && !applyClub(gameMeta.moves)) {
      // Status says ready but qualities not on payload yet — one sync.
      syncFromServer(gameMeta.id).then((g) => applyClub(g?.moves)).catch(() => {});
      setClubReviewHint(t('playing.clubReviewPending', {
        defaultValue: 'Club review is preparing move qualities…',
      }));
      return undefined;
    }

    if (moveCount < 1) {
      setClubReviewHint(t('playing.clubReviewNoMoves', {
        defaultValue: 'No moves played — club review is not available. Use Analyze for a local engine look.',
      }));
      return undefined;
    }
    if (status === 'SKIPPED' || moveCount < 6) {
      setClubReviewHint(t('playing.clubReviewTooShort', {
        defaultValue: 'This game is too short for automatic club review. Try Quick or Cloud analyze.',
      }));
      return undefined;
    }
    if (status === 'FAILED') {
      setClubReviewHint(t('playing.clubReviewFailed', {
        defaultValue: 'Club review failed. Retry club review, or run Quick / Cloud analyze.',
      }));
      return undefined;
    }
    if (status === 'PENDING' || status === 'RUNNING' || !status) {
      setClubReviewHint(t('playing.clubReviewPending', {
        defaultValue: 'Club review is preparing move qualities…',
      }));
      let tries = 0;
      const iv = window.setInterval(() => {
        tries += 1;
        if (tries > 12) {
          window.clearInterval(iv);
          setClubReviewHint(t('playing.clubReviewSlow', {
            defaultValue: 'Club review is taking longer than usual. You can Analyze locally meanwhile.',
          }));
          return;
        }
        syncFromServer(gameMeta.id)
          .then((g) => {
            if (applyClub(g?.moves) || g?.clubReviewReady) {
              applyClub(g?.moves);
              window.clearInterval(iv);
            } else {
              const st = String(g?.clubReviewStatus || '').toUpperCase();
              if (st === 'SKIPPED' || st === 'FAILED') {
                window.clearInterval(iv);
                setClubReviewHint(st === 'SKIPPED'
                  ? t('playing.clubReviewTooShort', {
                    defaultValue: 'This game is too short for automatic club review. Try Quick or Cloud analyze.',
                  })
                  : t('playing.clubReviewFailed', {
                    defaultValue: 'Club review failed. Retry club review, or run Quick / Cloud analyze.',
                  }));
              }
            }
          })
          .catch(() => { /* ignore */ });
      }, 2500);
      return () => window.clearInterval(iv);
    }
    setClubReviewHint(t('playing.clubReviewUnavailable', {
      defaultValue: 'Club review is not available for this game. Use Quick or Cloud analyze.',
    }));
    return undefined;
  }, [
    finished,
    gameMeta?.id,
    gameMeta?.gameType,
    gameMeta?.moves,
    gameMeta?.moveCount,
    gameMeta?.clubReviewStatus,
    gameMeta?.clubReviewReady,
    analysisSummary?.source,
    syncFromServer,
    fullHistory.length,
    t,
  ]);

  const onRequestClubReview = async () => {
    if (!gameMeta?.id || clubReviewBusy) return;
    setClubReviewBusy(true);
    setError('');
    try {
      const g = await requestClubReview(gameMeta.id);
      applyMeta(g);
      setClubReviewHint(t('playing.clubReviewPending', {
        defaultValue: 'Club review is preparing move qualities…',
      }));
    } catch (e) {
      setError(apiErrorMessage(e, t('playing.clubReviewRequestFailed', {
        defaultValue: 'Could not queue club review.',
      })));
    } finally {
      setClubReviewBusy(false);
    }
  };

  const mistakePlies = useMemo(
    () => mistakePliesFromQualities(qualitiesByPly),
    [qualitiesByPly],
  );

  const displayChess = useMemo(() => {
    if (viewPly == null || viewPly >= fullVerbose.length) return chess;
    const c = new Chess();
    for (let i = 0; i < viewPly; i++) {
      try {
        c.move(fullVerbose[i]);
      } catch {
        break;
      }
    }
    return c;
  }, [viewPly, chess, fullVerbose]);

  useEffect(() => {
    setResultDismissed(false);
    setAnalysisOn(false);
    setAnalysisByPly({});
    setAnalysisBusy(false);
    setAnalysisProgress(0);
    setAnalysisSummary(null);
    setRematchSent(false);
    setClubReviewHint('');
    deepLinkAppliedRef.current = false;
    plyUrlReadyRef.current = false;
  }, [gameMeta?.id]);

  useEffect(() => {
    // Follow live tip when new moves arrive unless user scrubbed back
    if (viewPly != null && viewPly < fullHistory.length) return;
    setViewPly(null);
  }, [fullHistory.length]);

  const goToPly = useCallback((ply) => {
    setResultDismissed(true);
    if (ply == null || ply >= fullHistory.length) setViewPly(null);
    else setViewPly(Math.max(0, ply));
  }, [fullHistory.length]);

  const jumpMistake = useCallback((dir) => {
    if (!mistakePlies.length) return;
    const cur = activePly;
    if (dir > 0) {
      const next = mistakePlies.find((p) => p > cur) ?? mistakePlies[0];
      goToPly(next >= fullHistory.length ? null : next);
      return;
    }
    const prev = [...mistakePlies].reverse().find((p) => p < cur) ?? mistakePlies[mistakePlies.length - 1];
    goToPly(prev >= fullHistory.length ? null : prev);
  }, [mistakePlies, activePly, fullHistory.length, goToPly]);

  const stepPly = useCallback((delta) => {
    if (!canScrub) return;
    const cur = viewPly == null ? fullHistory.length : viewPly;
    goToPly(cur + delta);
  }, [canScrub, viewPly, fullHistory.length, goToPly]);

  useEffect(() => {
    const onKey = (e) => {
      const tag = e.target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || e.target?.isContentEditable) return;
      if (e.key === '?' || (e.shiftKey && e.key === '/')) {
        e.preventDefault();
        setShortcutHelpOpen((o) => !o);
        return;
      }
      if (e.key === 'Escape') {
        if (shortcutHelpOpen) {
          setShortcutHelpOpen(false);
          return;
        }
        if (finished && !resultDismissed) {
          setResultDismissed(true);
          return;
        }
        setPendingPromotion(null);
        setConfirmResign(false);
        setConfirmAbort(false);
        setMoveFrom(null);
        setOptionSquares({});
        return;
      }
      if (e.key === 'f' || e.key === 'F') {
        e.preventDefault();
        setFlipOverride((o) => {
          if (o === 'white') return 'black';
          if (o === 'black') return 'white';
          return baseOrientation === 'white' ? 'black' : 'white';
        });
        return;
      }
      if ((e.key === 'r' || e.key === 'R') && !finished && !spectateOnly && !busy) {
        e.preventDefault();
        setConfirmResign(true);
        return;
      }
      if ((e.key === 'd' || e.key === 'D')
        && !finished
        && !spectateOnly
        && !busy
        && gameMeta?.gameType
        && gameMeta.gameType !== 'VS_COMPUTER'
        && gameMeta?.id
        && !gameMeta?.drawOfferedByMe) {
        e.preventDefault();
        setBusy(true);
        offerDraw(gameMeta.id)
          .then((meta) => applyMeta(meta))
          .catch((err) => setError(apiErrorMessage(err, t('playing.drawFailed'))))
          .finally(() => setBusy(false));
        return;
      }
      if (!canScrub || fullHistory.length === 0) return;
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        stepPly(-1);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        stepPly(1);
      } else if (e.key === 'Home') {
        e.preventDefault();
        goToPly(0);
      } else if (e.key === 'End') {
        e.preventDefault();
        goToPly(null);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [
    canScrub,
    stepPly,
    goToPly,
    finished,
    resultDismissed,
    fullHistory.length,
    spectateOnly,
    busy,
    shortcutHelpOpen,
    baseOrientation,
    gameMeta,
    applyMeta,
    t,
  ]);

  useEffect(() => {
    if (loading || deepLinkAppliedRef.current) return;
    // Number(null) === 0 — must treat a missing ?ply as "no deep link", not ply 0.
    const plyRaw = search.get('ply');
    const plyParam = plyRaw != null && plyRaw !== '' ? Number(plyRaw) : NaN;
    const wantAnalyze = search.get('analyze') === '1';
    const hasPly = Number.isFinite(plyParam) && plyParam >= 0;
    // Empty 0-move games still honor ?analyze=1; ply scrub needs history.
    if (fullHistory.length === 0 && !wantAnalyze) return;
    if (hasPly && fullHistory.length > 0) {
      const clamped = Math.min(plyParam, fullHistory.length);
      goToPly(clamped >= fullHistory.length ? null : clamped);
    }
    if (wantAnalyze) {
      deepLinkAppliedRef.current = true;
      plyUrlReadyRef.current = true;
      // Prefer cached cloud review; fall back to local deep when engine is ready.
      (async () => {
        if (gameMeta?.id && finished) {
          try {
            const dto = await getCachedCloudAnalysis(gameMeta.id);
            if (dto) {
              const mapped = cloudDtoToAnalysisState(dto);
              setAnalysisPreset('cloud');
              setAnalysisOn(true);
              setAnalysisByPly(mapped.byPly);
              setQualitiesByPly(mapped.qualities);
              setAnalysisSummary(mapped.summary);
              return;
            }
          } catch {
            /* fall through */
          }
        }
        if (!engineReady || !engineRef.current) {
          deepLinkAppliedRef.current = false;
          return;
        }
        void runAnalysis('deep');
      })();
    } else if (hasPly) {
      deepLinkAppliedRef.current = true;
      plyUrlReadyRef.current = true;
    } else {
      plyUrlReadyRef.current = true;
    }
  }, [loading, finished, fullHistory.length, search, goToPly, engineReady, gameMeta?.id]);

  // Keep address-bar ?ply= in sync while scrubbing (copy-link already encoded ply).
  useEffect(() => {
    if (loading || !gameMeta?.id || !plyUrlReadyRef.current) return;
    const atTip = viewPly == null || viewPly >= fullHistory.length;
    const desired = atTip ? null : String(viewPly);
    const current = search.get('ply');
    if (desired == null && (current == null || current === '')) return;
    if (desired != null && current === desired) return;
    const next = new URLSearchParams(search);
    if (desired == null) next.delete('ply');
    else next.set('ply', desired);
    setSearch(next, { replace: true });
  }, [viewPly, fullHistory.length, loading, gameMeta?.id, search, setSearch]);

  useEffect(() => {
    const ply = fullHistory.length;
    if (loading) {
      prevPlyRef.current = ply;
      return;
    }
    const prev = prevPlyRef.current;
    if (ply > prev) {
      const last = fullVerbose[ply - 1];
      if (last?.san?.includes('+') || last?.san?.includes('#')) playChessCue('check');
      else if (last?.captured) playChessCue('capture');
      else playChessCue('move');
    }
    prevPlyRef.current = ply;
  }, [fullHistory.length, fullVerbose, loading]);

  useEffect(() => {
    if (loading) {
      prevFinishedRef.current = !!finished;
      return;
    }
    const was = prevFinishedRef.current;
    if (finished && !was) playChessCue('end');
    prevFinishedRef.current = !!finished;
  }, [finished, loading, gameMeta?.id]);

  useEffect(() => {
    if (loading || finished || spectateOnly) {
      prevOfferRef.current = {
        draw: !!gameMeta?.drawOfferedByOpponent,
        takeback: !!gameMeta?.takebackOfferedByOpponent,
      };
      return;
    }
    const drawNow = !!gameMeta?.drawOfferedByOpponent;
    const takebackNow = !!gameMeta?.takebackOfferedByOpponent;
    if ((drawNow && !prevOfferRef.current.draw) || (takebackNow && !prevOfferRef.current.takeback)) {
      playChessCue('offer');
    }
    prevOfferRef.current = { draw: drawNow, takeback: takebackNow };
  }, [
    gameMeta?.drawOfferedByOpponent,
    gameMeta?.takebackOfferedByOpponent,
    finished,
    spectateOnly,
    loading,
  ]);

  const clearSelection = () => {
    setMoveFrom(null);
    setOptionSquares({});
  };

  const submitMove = async (from, to, promotion = 'q') => {
    if (spectateOnly || finished || busy || engineThinking) return false;
    if (gameMeta?.myColor && fenSide(chess.fen()) !== gameMeta.myColor) return false;
    const prevFen = chess.fen();
    const next = new Chess(chess.fen());
    let move;
    try {
      move = next.move({ from, to, promotion });
    } catch {
      return false;
    }
    if (!move) return false;
    setChess(next);
    // Mark optimistic position so REST/STOMP confirm does not remount the board.
    const optimisticCount = (Array.isArray(gameMetaRef.current?.moves)
      ? gameMetaRef.current.moves.length
      : next.history().length - 1) + 1;
    lastPosKeyRef.current = `${gameMeta.id}|${gameMeta.status || 'ACTIVE'}|${boardPosKey(next.fen(), optimisticCount)}`;
    const optimisticMove = {
      ply: optimisticCount,
      san: move.san,
      uci: `${move.from}${move.to}${move.promotion || ''}`,
      fenAfter: next.fen(),
    };
    const prevMeta = gameMetaRef.current || gameMeta;
    const optimisticMeta = {
      ...prevMeta,
      currentFen: next.fen(),
      pgn: next.pgn(),
      moveCount: optimisticCount,
      lastMove: optimisticMove,
      moves: [...(Array.isArray(prevMeta.moves) ? prevMeta.moves.filter((m) => Number(m.ply) < optimisticCount) : []), optimisticMove],
    };
    gameMetaRef.current = optimisticMeta;
    setViewPly(null);
    clearSelection();
    setPendingPromotion(null);
    setBusy(true);
    setError('');
    try {
      const updated = await postChessMove(gameMeta.id, {
        san: move.san,
        uci: `${move.from}${move.to}${move.promotion || ''}`,
        fenAfter: next.fen(),
        pgn: next.pgn(),
      });
      applyMeta(updated);
      await runComputer(next, updated);
    } catch (e) {
      setError(apiErrorMessage(e, t('playing.moveFailed')));
      // Snap back immediately so the reject doesn't feel like a flicker ghost.
      setChess(new Chess(prevFen));
      await syncFromServer(gameMeta.id);
    } finally {
      setBusy(false);
    }
    return true;
  };

  const requestMove = (from, to) => {
    if (!from || !to) return false;
    if (spectateOnly || finished || busy || engineThinking || pendingPromotion || viewingPast) return false;
    const myColor = gameMeta?.myColor;
    if (!myTurn) {
      if (!myColor) return false;
      if (needsPromotion(chess, from, to)) {
        if (autoQueen) {
          setPremove({ from, to, promotion: 'q' });
          clearSelection();
          return true;
        }
        setPendingPromotion({ from, to, color: myColor, premove: true });
        clearSelection();
        return 'promotion';
      }
      if (!isLegalPremove(chess, myColor, from, to, 'q')) return false;
      setPremove({ from, to, promotion: 'q' });
      clearSelection();
      return true;
    }
    const legal = chess.moves({ square: from, verbose: true }).some((m) => m.to === to);
    if (!legal) return false;
    if (needsPromotion(chess, from, to)) {
      if (autoQueen) {
        if (moveConfirmation) {
          setPendingMoveConfirm({ from, to, promotion: 'q' });
          clearSelection();
          return false;
        }
        return submitMove(from, to, 'q');
      }
      setPendingPromotion({ from, to, color: chess.get(from)?.color || 'w' });
      clearSelection();
      return 'promotion';
    }
    if (moveConfirmation) {
      setPendingMoveConfirm({ from, to });
      clearSelection();
      return false;
    }
    return submitMove(from, to, 'q');
  };

  const onDrop = async (sourceSquare, targetSquare) => {
    clearSelection();
    const result = requestMove(sourceSquare, targetSquare);
    // Returning true before FEN updates causes a ghost piece on promotion
    if (result === 'promotion') return false;
    return result;
  };

  const onPieceDragBegin = (_piece, sourceSquare) => {
    if (spectateOnly || finished || busy || engineThinking || pendingPromotion || viewingPast) {
      return;
    }
    const myColor = gameMeta?.myColor;
    if (!myTurn) {
      if (!myColor) return;
      const piece = chess.get(sourceSquare);
      if (!piece || piece.color !== myColor) return;
      const next = premoveOptionSquares(chess, myColor, sourceSquare);
      if (!next) return;
      setMoveFrom(sourceSquare);
      setOptionSquares(next);
      return;
    }
    const piece = chess.get(sourceSquare);
    if (!piece || piece.color !== sideToMove) return;
    const next = legalOptionSquares(chess, sourceSquare);
    if (!next) return;
    setMoveFrom(sourceSquare);
    setOptionSquares(next);
  };

  const onSquareClick = (square) => {
    if (spectateOnly || finished || busy || engineThinking || pendingPromotion || viewingPast) return;
    const myColor = gameMeta?.myColor;
    // Touch-friendly: tap either square of an armed premove to clear it.
    if (premove && (square === premove.from || square === premove.to) && !moveFrom) {
      setPremove(null);
      clearSelection();
      return;
    }
    if (!myTurn) {
      if (!myColor) return;
      if (!moveFrom) {
        const piece = chess.get(square);
        if (!piece || piece.color !== myColor) return;
        const next = premoveOptionSquares(chess, myColor, square);
        if (!next) return;
        setMoveFrom(square);
        setOptionSquares(next);
        return;
      }
      if (moveFrom === square) {
        clearSelection();
        setPremove(null);
        return;
      }
      const retarget = resolveOwnPieceRetarget({
        chess,
        moveFrom,
        square,
        ownColor: myColor,
        buildOptions: (c, sq) => premoveOptionSquares(c, myColor, sq),
      });
      if (retarget?.action === 'select') {
        setMoveFrom(retarget.square);
        setOptionSquares(retarget.optionSquares);
        return;
      }
      if (retarget?.action === 'clear') {
        clearSelection();
        setPremove(null);
        return;
      }
      requestMove(moveFrom, square);
      return;
    }
    if (!moveFrom) {
      const piece = chess.get(square);
      if (!piece || piece.color !== sideToMove) return;
      const next = legalOptionSquares(chess, square);
      if (!next) return;
      setMoveFrom(square);
      setOptionSquares(next);
      return;
    }
    if (moveFrom === square) {
      clearSelection();
      return;
    }
    const retarget = resolveOwnPieceRetarget({
      chess,
      moveFrom,
      square,
      ownColor: sideToMove,
      buildOptions: legalOptionSquares,
    });
    if (retarget?.action === 'select') {
      setMoveFrom(retarget.square);
      setOptionSquares(retarget.optionSquares);
      return;
    }
    if (retarget?.action === 'clear') {
      clearSelection();
      return;
    }
    requestMove(moveFrom, square);
  };

  const onSquareRightClick = () => {
    setPremove(null);
    clearSelection();
  };

  // Fire queued premove when it becomes our turn
  useEffect(() => {
    if (!myTurn || !premove || spectateOnly || finished || busy || engineThinking || premovePendingRef.current) {
      return undefined;
    }
    const { from, to, promotion } = premove;
    const legal = chess.moves({ square: from, verbose: true }).some((m) => m.to === to);
    if (!legal) {
      setPremove(null);
      return undefined;
    }
    premovePendingRef.current = true;
    setPremove(null);
    void (async () => {
      try {
        await submitMove(from, to, promotion || 'q');
      } finally {
        premovePendingRef.current = false;
      }
    })();
    return undefined;
  }, [myTurn, premove, chess, spectateOnly, finished, busy, engineThinking]);

  const onResign = async () => {
    if (!gameMeta?.id || finished || spectateOnly || busy) return;
    if (!confirmResign) {
      setConfirmResign(true);
      setConfirmAbort(false);
      return;
    }
    setConfirmResign(false);
    setBusy(true);
    try {
      applyMeta(await resignChessGame(gameMeta.id));
    } catch (e) {
      setError(apiErrorMessage(e, t('playing.resignFailed')));
    } finally {
      setBusy(false);
    }
  };

  const onDraw = async () => {
    if (!gameMeta?.id || finished || spectateOnly || busy) return;
    setBusy(true);
    try {
      applyMeta(await offerDraw(gameMeta.id));
    } catch (e) {
      setError(apiErrorMessage(e, t('playing.drawFailed')));
    } finally {
      setBusy(false);
    }
  };

  const onClaimDraw = async () => {
    if (!gameMeta?.id || finished || spectateOnly || !gameMeta.canClaimDraw) return;
    setBusy(true);
    try {
      applyMeta(await claimDraw(gameMeta.id));
    } catch (e) {
      setError(apiErrorMessage(e, t('playing.claimDrawFailed', { defaultValue: 'Could not claim draw.' })));
    } finally {
      setBusy(false);
    }
  };

  const reportReasons = useMemo(() => ([
    { id: 'SUSPECTED_ASSISTANCE', label: t('playing.reportReason.assistance', { defaultValue: 'Suspected engine / assistance' }) },
    { id: 'ABUSIVE_CHAT', label: t('playing.reportReason.chat', { defaultValue: 'Abusive chat' }) },
    { id: 'STALLING', label: t('playing.reportReason.stalling', { defaultValue: 'Stalling / refusing to play' }) },
    { id: 'OTHER', label: t('playing.reportReason.other', { defaultValue: 'Other' }) },
  ]), [t]);

  const openReportModal = () => {
    if (!gameMeta?.id || reportBusy || spectateOnly) return;
    const me = getUserId();
    const opponentId = me != null && Number(me) === Number(gameMeta.whiteUserId)
      ? gameMeta.blackUserId
      : gameMeta.whiteUserId;
    if (!opponentId) return;
    setReportReason('SUSPECTED_ASSISTANCE');
    setReportDetails('');
    setReportOpen(true);
  };

  const submitReport = async () => {
    if (!gameMeta?.id || reportBusy || spectateOnly) return;
    const me = getUserId();
    const opponentId = me != null && Number(me) === Number(gameMeta.whiteUserId)
      ? gameMeta.blackUserId
      : gameMeta.whiteUserId;
    if (!opponentId) return;
    setReportBusy(true);
    setReportNotice('');
    try {
      await reportChessPlayer({
        reportedUserId: opponentId,
        gameId: gameMeta.id,
        reason: reportReason,
        details: reportDetails || '',
      });
      setReportOpen(false);
      setReportNotice(t('playing.reportSent', { defaultValue: 'Report submitted. Staff will review.' }));
    } catch (e) {
      setError(apiErrorMessage(e, t('playing.reportFailed', { defaultValue: 'Could not submit report.' })));
    } finally {
      setReportBusy(false);
    }
  };

  const onReportOpponent = () => {
    openReportModal();
  };

  const onDeclineDraw = async () => {
    if (!gameMeta?.id || spectateOnly) return;
    setBusy(true);
    try {
      applyMeta(await declineDraw(gameMeta.id));
    } catch (e) {
      setError(apiErrorMessage(e, t('playing.drawFailed')));
    } finally {
      setBusy(false);
    }
  };

  const onTakeback = async () => {
    if (!gameMeta?.id || finished || spectateOnly || engineThinking) return;
    computerGenRef.current += 1;
    computerBusyRef.current = false;
    setEngineThinking(false);
    setBusy(true);
    try {
      applyMeta(await offerTakeback(gameMeta.id));
    } catch (e) {
      setError(apiErrorMessage(e, t('playing.takebackFailed')));
    } finally {
      setBusy(false);
    }
  };

  const onDeclineTakeback = async () => {
    if (!gameMeta?.id || spectateOnly) return;
    setBusy(true);
    try {
      applyMeta(await declineTakeback(gameMeta.id));
    } catch (e) {
      setError(apiErrorMessage(e, t('playing.takebackFailed')));
    } finally {
      setBusy(false);
    }
  };

  const submitTypedMove = async () => {
    if (spectateOnly || finished || busy || engineThinking || !myTurn || pendingPromotion || viewingPast) return;
    const raw = (moveText || '').trim();
    if (!raw) return;
    setError('');
    const uci = raw.toLowerCase().replace(/[^a-h1-8qrbn]/gi, '');
    if (/^[a-h][1-8][a-h][1-8][qrbn]?$/.test(uci)) {
      const from = uci.slice(0, 2);
      const to = uci.slice(2, 4);
      const promo = uci[4] || undefined;
      setMoveText('');
      if (promo) {
        await submitMove(from, to, promo);
      } else {
        const result = requestMove(from, to);
        if (result === false) setError(t('playing.moveFailed'));
      }
      return;
    }
    const next = new Chess(chess.fen());
    let move;
    try {
      move = next.move(raw);
    } catch {
      move = null;
    }
    if (!move) {
      setError(t('playing.moveFailed'));
      return;
    }
    setMoveText('');
    await submitMove(move.from, move.to, move.promotion || 'q');
  };

  const sendChat = async (e) => {
    e?.preventDefault?.();
    if (!gameMeta?.id || gameMeta.gameType === 'VS_COMPUTER' || spectateOnly || chatBusy) return;
    const text = (chatDraft || '').trim();
    if (!text) return;
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      setChatError(t('playing.chatOffline', {
        defaultValue: 'You are offline — reconnect to send chat.',
      }));
      return;
    }
    setChatBusy(true);
    setChatError('');
    try {
      const msg = await postChessChat(gameMeta.id, text);
      setChatDraft('');
      if (msg?.id) {
        setChatMessages((prev) => (prev.some((m) => m.id === msg.id) ? prev : [...prev, msg]));
      }
    } catch (err) {
      setChatError(apiErrorMessage(err, t('playing.chatSendFailed')));
    } finally {
      setChatBusy(false);
    }
  };

  const onAbort = async () => {
    if (!gameMeta?.id || finished || spectateOnly || busy) return;
    // Waiting room cancel: no second-step confirm (Lichess-style)
    if (gameMeta.status !== 'PENDING' && !confirmAbort) {
      setConfirmAbort(true);
      setConfirmResign(false);
      return;
    }
    setConfirmAbort(false);
    setBusy(true);
    pendingLeaveRef.current.aborting = true;
    try {
      applyMeta(await abortChessGame(gameMeta.id));
      if (gameMeta.status === 'PENDING') {
        pendingLeaveRef.current = { gameId: null, isSender: false, aborting: false };
        navigate('/playing');
      }
    } catch (e) {
      pendingLeaveRef.current.aborting = false;
      setError(apiErrorMessage(e, t('playing.abortFailed')));
    } finally {
      setBusy(false);
    }
  };

  /** Challenger Back hub / soft leave — cancel waiting challenge so it is not orphaned. */
  const leaveWaitingBoard = useCallback(async () => {
    const meta = gameMeta;
    if (!meta?.id || meta.status !== 'PENDING' || spectateOnly) {
      navigate('/playing');
      return;
    }
    const me = getUserId();
    const isSender = me != null
      && meta.challengeFromUserId != null
      && Number(me) === Number(meta.challengeFromUserId);
    if (!isSender) {
      navigate('/playing');
      return;
    }
    if (pendingLeaveRef.current.aborting) {
      navigate('/playing');
      return;
    }
    pendingLeaveRef.current.aborting = true;
    try {
      await abortChessGame(meta.id);
      pendingLeaveRef.current = { gameId: null, isSender: false, aborting: false };
      navigate('/playing');
    } catch (e) {
      pendingLeaveRef.current.aborting = false;
      setError(apiErrorMessage(e, t('playing.abortFailed')));
    }
  }, [gameMeta, spectateOnly, navigate, t]);

  const onJoinChallenge = async () => {
    if (String(getActiveRole() || '').toUpperCase() === 'PARENT') return;
    if (!gameMeta?.pendingChallengeId || busy) return;
    setBusy(true);
    setError('');
    try {
      applyMeta(await acceptChallenge(gameMeta.pendingChallengeId));
    } catch (e) {
      setError(apiErrorMessage(e, t('playing.challengeAcceptFailed')));
    } finally {
      setBusy(false);
    }
  };

  const onDeclineChallengeFromBoard = async () => {
    if (!gameMeta?.pendingChallengeId || busy) return;
    setBusy(true);
    setError('');
    try {
      await declineChallenge(gameMeta.pendingChallengeId);
      pendingLeaveRef.current = { gameId: null, isSender: false, aborting: false };
      navigate('/playing');
    } catch (e) {
      setError(apiErrorMessage(e, t('playing.challengeDeclineFailed', { defaultValue: 'Could not decline. Try again.' })));
    } finally {
      setBusy(false);
    }
  };

  const exportPgnText = () => {
    const incSec = gameMeta?.incrementMs != null
      ? Math.round(Number(gameMeta.incrementMs) / 1000)
      : 0;
    return buildTaggedPgn({
      // Clean SAN movetext only — standard interchange PGN (not study annotations).
      moveText: chess.pgn({ maxWidth: 0, newline: ' ' }) || gameMeta?.pgn || '',
      whiteName: gameMeta?.whiteName || '?',
      blackName: gameMeta?.blackName || '?',
      status: gameMeta?.status,
      event: pgnEventLabel(gameMeta?.gameType, !!gameMeta?.rated),
      site: 'ChessDesk',
      round: '?',
      dateIso: gameMeta?.endedAt || gameMeta?.startedAt || gameMeta?.createdAt,
      eco: openingInfo?.eco,
      opening: openingInfo?.name,
      timeControl: pgnTimeControlTag(gameMeta?.timeControlSeconds, incSec),
      whiteElo: gameMeta?.whiteRatingBefore,
      blackElo: gameMeta?.blackRatingBefore,
      termination: pgnTerminationTag(gameMeta?.resultReason),
      includeAnnotations: false,
    });
  };

  const downloadPgn = () => {
    const pgn = exportPgnText();
    if (!pgn?.trim()) return;
    const blob = new Blob([pgn], { type: 'application/x-chess-pgn' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `game-${gameMeta?.id || 'chess'}.pgn`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const copyGameLink = async () => {
    if (!gameMeta?.id) return;
    const ply = viewPly == null ? fullHistory.length : viewPly;
    const params = new URLSearchParams();
    if (ply > 0) params.set('ply', String(ply));
    if (analysisOn || finished) params.set('analyze', '1');
    // Share spectate URLs for public boards so clubmates are not bounced by getGame.
    const shareable = gameMeta.spectatorAllowed !== false
      && String(gameMeta.gameType || '').toUpperCase() !== 'VS_COMPUTER';
    if (!shareable) {
      setChallengeDeclinedNotice(t('playing.privateLinkHint', {
        defaultValue: 'This game is private — the copied link only works for the two players (and staff).',
      }));
      setTimeout(() => setChallengeDeclinedNotice(''), 4500);
    }
    const base = (shareable || spectateOnly)
      ? `${window.location.origin}/playing/spectate/${gameMeta.id}`
      : `${window.location.origin}/playing/game/${gameMeta.id}`;
    const qs = params.toString();
    const url = qs ? `${base}?${qs}` : base;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2200);
    } catch {
      setError(t('playing.copyFailed'));
    }
  };

  const rematchComputer = () => {
    // Flip color vs AI (mirrors human rematch).
    const color = gameMeta?.myColor === 'b' ? 'white' : 'black';
    const level = gameMeta?.computerLevel || 3;
    const rated = gameMeta?.rated ? '1' : '0';
    const tc = gameMeta?.timeControlSeconds || 600;
    const inc = gameMeta?.incrementMs != null ? Math.round(gameMeta.incrementMs / 1000) : 0;
    navigate(`/playing/play?color=${color}&level=${level}&rated=${rated}&tc=${tc}&inc=${inc}`);
  };

  const rematchHuman = async () => {
    if (String(getActiveRole() || '').toUpperCase() === 'PARENT') return;
    if (!gameMeta?.id || rematchBusy) return;
    setRematchBusy(true);
    setError('');
    setChallengeDeclinedNotice('');
    try {
      const ch = await rematchGame(gameMeta.id);
      if (ch?.id) setPendingRematchChallengeId(ch.id);
      if (ch?.gameId) {
        navigate(`/playing/game/${ch.gameId}`);
        return;
      }
      setRematchSent(true);
      setResultDismissed(true);
    } catch (e) {
      setError(apiErrorMessage(e, t('playing.rematchFailed')));
    } finally {
      setRematchBusy(false);
    }
  };

  const cancelPendingRematch = async () => {
    if (!pendingRematchChallengeId || rematchBusy) return;
    setRematchBusy(true);
    try {
      await cancelChallenge(pendingRematchChallengeId);
      setPendingRematchChallengeId(null);
      setRematchSent(false);
    } catch (e) {
      setError(apiErrorMessage(e, t('playing.cancelChallengeFailed')));
    } finally {
      setRematchBusy(false);
    }
  };

  const acceptIncomingRematch = async () => {
    if (String(getActiveRole() || '').toUpperCase() === 'PARENT') return;
    if (!incomingRematchChallenge?.id || rematchBusy) return;
    setRematchBusy(true);
    try {
      const g = await acceptChallenge(incomingRematchChallenge.id);
      setIncomingRematchChallenge(null);
      navigate(`/playing/game/${g.id}`);
    } catch (e) {
      setError(apiErrorMessage(e, t('playing.challengeAcceptFailed')));
    } finally {
      setRematchBusy(false);
    }
  };

  const declineIncomingRematch = async () => {
    if (!incomingRematchChallenge?.id || rematchBusy) return;
    setRematchBusy(true);
    try {
      await declineChallenge(incomingRematchChallenge.id);
      setIncomingRematchChallenge(null);
    } catch (e) {
      setError(apiErrorMessage(e, t('playing.challengeDeclineFailed', {
        defaultValue: 'Could not decline. Try again.',
      })));
    } finally {
      setRematchBusy(false);
    }
  };

  const runAnalysis = async (presetKey = analysisPreset) => {
    if (!engineRef.current || analysisBusy) return;
    const preset = ANALYSIS_PRESETS[presetKey] || ANALYSIS_PRESETS.deep;
    const token = ++analysisAbortRef.current;
    setAnalysisPreset(presetKey);
    setAnalysisOn(true);
    setAnalysisBusy(true);
    setAnalysisProgress(0);
    setAnalysisSummary(null);
    setQualitiesByPly({});
    setResultDismissed(true);
    const verbose = chess.history({ verbose: true });
    const byPly = {};
    try {
      const cursor = new Chess();
      const total = verbose.length + 1;
      for (let ply = 0; ply <= verbose.length; ply++) {
        if (token !== analysisAbortRef.current) return;
        if (ply > 0) {
          try {
            cursor.move(verbose[ply - 1]);
          } catch {
            break;
          }
        }
        try {
          const side = fenSide(cursor.fen());
          const raw = await engineRef.current.evaluateFen(cursor.fen(), preset.depth, preset.multipv);
          if (token !== analysisAbortRef.current) return;
          byPly[ply] = toWhiteEval(raw, side);
        } catch {
          /* skip */
        }
        setAnalysisByPly({ ...byPly });
        setAnalysisProgress(Math.round(((ply + 1) / total) * 100));
      }
      if (token !== analysisAbortRef.current) return;
      const result = summarizeAnalysis(byPly, verbose);
      setAnalysisByPly({ ...result.byPly });
      setQualitiesByPly(result.qualities);
      setAnalysisSummary({ ...result.summary, depth: preset.depth, source: 'local' });
      if (gameMeta?.id) {
        const tipPly = activePly;
        const tip = result.byPly[tipPly] || result.byPly[verbose.length]
          || result.byPly[Object.keys(result.byPly).length - 1];
        if (tip) {
          shareChessAnalysis(gameMeta.id, {
            ply: tipPly,
            evalCp: tip.evalCp ?? tip.scoreCp ?? null,
            bestUci: tip.bestMove || tip.bestUci || null,
            pv: tip.pv || tip.pvs?.[0] || [],
          }).catch(() => { /* optional share */ });
        }
      }
    } finally {
      if (token === analysisAbortRef.current) {
        setAnalysisBusy(false);
        setAnalysisProgress(100);
      }
    }
  };

  const runServerCloudAnalysis = async () => {
    if (!gameMeta?.id || analysisBusy) return;
    const token = ++analysisAbortRef.current;
    setAnalysisPreset('cloud');
    setAnalysisOn(true);
    setAnalysisBusy(true);
    setAnalysisProgress(15);
    setResultDismissed(true);
    setError('');
    try {
      let dto = null;
      try {
        dto = await getCachedCloudAnalysis(gameMeta.id);
      } catch {
        dto = null;
      }
      if (!dto) {
        setAnalysisProgress(35);
        dto = await runCloudAnalysis(gameMeta.id);
      }
      if (token !== analysisAbortRef.current) return;
      const mapped = cloudDtoToAnalysisState(dto);
      setAnalysisByPly(mapped.byPly);
      setQualitiesByPly(mapped.qualities);
      setAnalysisSummary(mapped.summary);
      setAnalysisProgress(100);
    } catch (e) {
      if (token !== analysisAbortRef.current) return;
      setError(apiErrorMessage(e, t('playing.cloudAnalysisFailed')));
      setAnalysisOn(false);
      setAnalysisByPly({});
      setAnalysisSummary(null);
      setQualitiesByPly({});
    } finally {
      if (token === analysisAbortRef.current) {
        setAnalysisBusy(false);
      }
    }
  };

  const cancelAnalysis = () => {
    analysisAbortRef.current += 1;
    try {
      engineRef.current?.stop?.();
    } catch {
      /* ignore */
    }
    setAnalysisBusy(false);
  };

  const humanGame = gameMeta?.gameType && gameMeta.gameType !== 'VS_COMPUTER';
  const plyCount = chess.history()?.length || 0;
  const firstMoveSecsLeft = (() => {
    if (finished || !humanGame || !gameMeta?.firstMoveDeadlineAt) return null;
    if (Number(plyCount) > 0 || Number(gameMeta.moveCount || 0) > 0) return null;
    const deadline = parseApiDateMs(gameMeta.firstMoveDeadlineAt);
    if (!Number.isFinite(deadline)) return null;
    return Math.max(0, Math.ceil((deadline - firstMoveNow) / 1000));
  })();
  const canUseCloudAnalysis = useMemo(() => {
    if (!finished || !gameMeta?.id) return false;
    const roles = getRoles().map((r) => String(r).toUpperCase().replace(/^ROLE_/, ''));
    const staff = roles.some((r) => r === 'ADMIN' || r === 'MANAGER' || r === 'COACH');
    if (staff) return true;
    const me = Number(getUserId());
    return me != null && !Number.isNaN(me)
      && (me === Number(gameMeta.whiteUserId) || me === Number(gameMeta.blackUserId) || !!gameMeta.myColor);
  }, [finished, gameMeta]);
  const canAbort = !finished && (
    gameMeta?.status === 'PENDING'
    || plyCount <= 2
    || (gameMeta?.gameType === 'VS_COMPUTER' && !gameMeta?.rated)
  );
  const waitingToJoin = !finished && !spectateOnly && gameMeta?.status === 'PENDING';
  const meId = getUserId();
  const isParentRole = String(getActiveRole() || '').toUpperCase() === 'PARENT'
    || (getRoles() || []).some((r) => String(r).toUpperCase().replace(/^ROLE_/, '') === 'PARENT');
  const isChallengeInvitee = waitingToJoin
    && meId != null
    && gameMeta?.challengeToUserId != null
    && Number(meId) === Number(gameMeta.challengeToUserId);
  const isChallengeSender = waitingToJoin
    && meId != null
    && gameMeta?.challengeFromUserId != null
    && Number(meId) === Number(gameMeta.challengeFromUserId);
  const waitingOpponentName = (() => {
    if (!waitingToJoin || meId == null) return gameMeta?.blackName || gameMeta?.whiteName || '';
    if (Number(meId) === Number(gameMeta?.whiteUserId)) return gameMeta?.blackName || '';
    if (Number(meId) === Number(gameMeta?.blackUserId)) return gameMeta?.whiteName || '';
    return gameMeta?.blackName || gameMeta?.whiteName || '';
  })();
  const waitingOpponentId = (() => {
    if (!waitingToJoin || meId == null) return gameMeta?.blackUserId || gameMeta?.whiteUserId;
    if (Number(meId) === Number(gameMeta?.whiteUserId)) return gameMeta?.blackUserId;
    if (Number(meId) === Number(gameMeta?.blackUserId)) return gameMeta?.whiteUserId;
    return gameMeta?.blackUserId || gameMeta?.whiteUserId;
  })();
  const tcLabel = formatTimeControl(
    gameMeta?.timeControlSeconds,
    gameMeta?.incrementMs != null ? Math.round(gameMeta.incrementMs / 1000) : 0,
    gameMeta?.daysPerMove,
  );
  const moveDeadlineCountdown = formatDeadlineCountdown(gameMeta?.moveDeadlineAt, deadlineNow);
  const moveDeadlineUrgency = deadlineUrgency(gameMeta?.moveDeadlineAt, deadlineNow);
  const moveDeadlineLabel = moveDeadlineCountdown
    || (gameMeta?.moveDeadlineAt ? formatDateTime(gameMeta.moveDeadlineAt) : null);
  const statusLabel = gameMeta?.status
    ? t(`playing.status.${gameMeta.status}`, { defaultValue: gameMeta.status })
    : '…';
  const boardLiveStatus = finished
    ? statusLabel
    : `${turnLabel}${inCheck ? ` — ${t('playing.check', { defaultValue: 'Check' })}` : ''}${myTurn ? ` — ${t('playing.yourTurn', { defaultValue: 'Your turn' })}` : ''}`;
  const reasonLabel = formatResultReason(gameMeta?.resultReason, t);
  const material = materialSnapshot(displayChess);
  const whiteRatingLine = formatRatingChange(
    gameMeta?.whiteRatingBefore,
    finished ? gameMeta?.whiteRatingAfter : null,
  );
  const blackRatingLine = formatRatingChange(
    gameMeta?.blackRatingBefore,
    finished ? gameMeta?.blackRatingAfter : null,
  );
  const myIsWhite = gameMeta?.myColor === 'w';
  const myRatingLine = myIsWhite ? whiteRatingLine : blackRatingLine;

  const topIsBlack = orientation === 'white';
  const top = {
    name: topIsBlack ? gameMeta?.blackName : gameMeta?.whiteName,
    userId: topIsBlack ? gameMeta?.blackUserId : gameMeta?.whiteUserId,
    rating: topIsBlack ? gameMeta?.blackRatingBefore : gameMeta?.whiteRatingBefore,
    ratingAfter: finished
      ? (topIsBlack ? gameMeta?.blackRatingAfter : gameMeta?.whiteRatingAfter)
      : null,
    clock: topIsBlack ? blackMs : whiteMs,
    active: !finished && !viewingPast && sideToMove === (topIsBlack ? 'b' : 'w'),
    color: topIsBlack ? 'black' : 'white',
    captured: topIsBlack ? material.blackCaptured : material.whiteCaptured,
    materialAdv: topIsBlack
      ? (material.whiteAdvantage < 0 ? -material.whiteAdvantage : null)
      : (material.whiteAdvantage > 0 ? material.whiteAdvantage : null),
  };
  const bottom = {
    name: topIsBlack ? gameMeta?.whiteName : gameMeta?.blackName,
    userId: topIsBlack ? gameMeta?.whiteUserId : gameMeta?.blackUserId,
    rating: topIsBlack ? gameMeta?.whiteRatingBefore : gameMeta?.blackRatingBefore,
    ratingAfter: finished
      ? (topIsBlack ? gameMeta?.whiteRatingAfter : gameMeta?.blackRatingAfter)
      : null,
    clock: topIsBlack ? whiteMs : blackMs,
    active: !finished && !viewingPast && sideToMove === (topIsBlack ? 'w' : 'b'),
    color: topIsBlack ? 'white' : 'black',
    captured: topIsBlack ? material.whiteCaptured : material.blackCaptured,
    materialAdv: topIsBlack
      ? (material.whiteAdvantage > 0 ? material.whiteAdvantage : null)
      : (material.whiteAdvantage < 0 ? -material.whiteAdvantage : null),
  };

  const promoColor = pendingPromotion?.color === 'b' ? 'black' : 'white';
  const promoPieces = [
    { key: 'q', label: promoColor === 'white' ? '♕' : '♛' },
    { key: 'r', label: promoColor === 'white' ? '♖' : '♜' },
    { key: 'b', label: promoColor === 'white' ? '♗' : '♝' },
    { key: 'n', label: promoColor === 'white' ? '♘' : '♞' },
  ];

  const customPieces = useMemo(() => customPiecesForSet(pieceSet), [pieceSet]);

  const squareStyles = (() => {
    const base = buildSquareStyles(displayChess, viewingPast || analysisOn ? {} : optionSquares);
    Object.assign(base, premoveSquareStyles(premove));
    if (pendingPromotion) {
      base[pendingPromotion.from] = {
        ...(base[pendingPromotion.from] || {}),
        backgroundColor: 'rgba(59, 130, 246, 0.4)',
      };
      base[pendingPromotion.to] = {
        ...(base[pendingPromotion.to] || {}),
        backgroundColor: 'rgba(250, 204, 21, 0.55)',
      };
    }
    const arrowSrc = analysisOn
      ? analysisByPly[activePly]
      : (sharedAnalysis?.bestUci ? { bestMove: sharedAnalysis.bestUci } : null);
    if (arrowSrc?.bestMove && arrowSrc.bestMove.length >= 4) {
      const from = arrowSrc.bestMove.slice(0, 2);
      const to = arrowSrc.bestMove.slice(2, 4);
      base[from] = {
        ...(base[from] || {}),
        backgroundColor: analysisOn ? 'rgba(56, 189, 248, 0.55)' : 'rgba(168, 85, 247, 0.45)',
      };
      base[to] = {
        ...(base[to] || {}),
        backgroundColor: analysisOn ? 'rgba(14, 165, 233, 0.7)' : 'rgba(147, 51, 234, 0.55)',
      };
    } else if (!analysisOn && bookHintCandidates.length) {
      bookHintCandidates.forEach((c, i) => {
        if (!c?.uci || c.uci.length < 4) return;
        const from = c.uci.slice(0, 2);
        const to = c.uci.slice(2, 4);
        const fromAlpha = i === 0 ? 0.4 : 0.22;
        const toAlpha = i === 0 ? 0.55 : 0.32;
        base[from] = {
          ...(base[from] || {}),
          backgroundColor: `rgba(34, 197, 94, ${fromAlpha})`,
        };
        base[to] = {
          ...(base[to] || {}),
          backgroundColor: `rgba(22, 163, 74, ${toAlpha})`,
        };
      });
    }
    return base;
  })();
  const bookHintArrows = (!analysisOn && !sharedAnalysis?.bestUci && bookHintCandidates[0]?.uci?.length >= 4)
    ? [[
      bookHintCandidates[0].uci.slice(0, 2),
      bookHintCandidates[0].uci.slice(2, 4),
      'rgba(34, 197, 94, 0.9)',
    ]]
    : [];
  const piecesLocked = spectateOnly || finished || busy || engineThinking || !!pendingPromotion || viewingPast || waitingToJoin;
  const canDragPieces = !piecesLocked && (myTurn || (!!gameMeta?.myColor && !finished));
  const showResultOverlay = finished && !resultDismissed && !viewingPast;
  const showWaitingOverlay = waitingToJoin && !viewingPast;
  const currentEval = analysisOn
    ? analysisByPly[activePly]
    : (sharedAnalysis?.evalCp != null
      ? { evalCp: sharedAnalysis.evalCp, bestMove: sharedAnalysis.bestUci }
      : null);

  return (
    <div className="page-shell-form playing-page">
      <PageHeaderSection
        eyebrow={t('nav.playingPlatform')}
        title={spectateOnly ? t('playing.spectateTitle') : t('playing.playTitle')}
        subtitle={
          gameMeta
            ? (
              <>
                <UserLink id={gameMeta.whiteUserId} name={gameMeta.whiteName} playing className="text-slate-600 hover:text-primary-700" />
                {' vs '}
                <UserLink id={gameMeta.blackUserId} name={gameMeta.blackName} playing className="text-slate-600 hover:text-primary-700" />
                {tcLabel ? ` · ${tcLabel}` : ''}
              </>
            )
            : t('playing.playSubtitle')
        }
        aside={(
          <div className="flex flex-wrap items-center justify-end gap-2">
            {gameMeta?.rated && <span className="playing-meta-pill">{t('playing.ratedGame')}</span>}
            {tcLabel && <span className="playing-meta-pill">{tcLabel}</span>}
            {moveDeadlineLabel && !finished && (
              <span
                className={`playing-meta-pill ${
                  moveDeadlineUrgency === 'critical'
                    ? 'playing-meta-pill--danger'
                    : moveDeadlineUrgency === 'warn'
                      ? 'playing-meta-pill--warn'
                      : ''
                }`}
              >
                {t('playing.moveDeadline', { when: moveDeadlineLabel })}
              </span>
            )}
            <PlayingStatusChip status={gameMeta?.status} label={statusLabel} />
          </div>
        )}
      />

      <div className="playing-page-alert-slot" aria-live="polite">
        {error && <PlayingBanner tone="danger">{error}</PlayingBanner>}
        {clubReviewHint && finished && humanGame && analysisSummary?.source !== 'local' && analysisSummary?.source !== 'cloud' && (
          <PlayingBanner tone="warning">
            <p>{clubReviewHint}</p>
            <div className="mt-2 flex flex-wrap gap-2">
              {(String(gameMeta?.clubReviewStatus || '').toUpperCase() === 'FAILED'
                || !gameMeta?.clubReviewStatus) && (gameMeta?.moveCount == null || Number(gameMeta.moveCount) >= 6) && (
                <Button variant="page-secondary" disabled={clubReviewBusy} onClick={onRequestClubReview}>
                  {clubReviewBusy
                    ? t('playing.loading')
                    : t('playing.retryClubReview', { defaultValue: 'Retry club review' })}
                </Button>
              )}
              <Button
                variant="page-secondary"
                disabled={analysisBusy}
                onClick={() => runAnalysis('quick')}
              >
                {t('playing.analysisQuick')}
              </Button>
              {canUseCloudAnalysis && (
                <Button
                  variant="page-secondary"
                  disabled={analysisBusy}
                  onClick={runServerCloudAnalysis}
                >
                  {t('playing.analysisCloud')}
                </Button>
              )}
            </div>
          </PlayingBanner>
        )}
        {challengeDeclinedNotice && (
          <PlayingBanner tone="warning">{challengeDeclinedNotice}</PlayingBanner>
        )}
        {shareNotice && <PlayingBanner tone="success">{shareNotice}</PlayingBanner>}
        {wsStatus === 'disconnected' && gameMeta && String(gameMeta.gameType || '').toUpperCase() !== 'VS_COMPUTER' && (
          <PlayingBanner tone="warning">{t('playing.reconnecting')}</PlayingBanner>
        )}
        {shortcutHelpOpen && (
          <div
            className="list-table-card mb-3 p-4 text-sm text-slate-700"
            role="dialog"
            aria-modal="true"
            aria-label={t('playing.shortcutHelpTitle')}
          >
            <div className="mb-2 flex items-center justify-between gap-2">
              <p className="font-semibold text-slate-900">{t('playing.shortcutHelpTitle')}</p>
              <Button variant="page-secondary" onClick={() => setShortcutHelpOpen(false)}>
                {t('playing.dismissResult', { defaultValue: 'Dismiss' })}
              </Button>
            </div>
            <ul className="grid gap-1.5 text-xs sm:grid-cols-2">
              <li><kbd className="font-mono">← →</kbd> {t('playing.shortcutScrub')}</li>
              <li><kbd className="font-mono">Home / End</kbd> {t('playing.shortcutEnds')}</li>
              <li><kbd className="font-mono">F</kbd> {t('playing.shortcutFlip', { defaultValue: 'Flip board' })}</li>
              {!spectateOnly && !finished && (
                <li><kbd className="font-mono">R</kbd> {t('playing.shortcutResign', { defaultValue: 'Resign (confirm)' })}</li>
              )}
              {!spectateOnly && !finished && humanGame && (
                <li><kbd className="font-mono">D</kbd> {t('playing.shortcutDraw', { defaultValue: 'Offer draw' })}</li>
              )}
              <li><kbd className="font-mono">?</kbd> / Esc {t('playing.shortcutToggleHelp')}</li>
            </ul>
          </div>
        )}
      </div>

      {loading && !gameMeta ? (
        <PlayingBoardSkeleton />
      ) : !gameMeta ? (
        <div className="list-table-card p-6 text-center">
          <p className="text-sm text-slate-600">{error || t('playing.loadFailed')}</p>
          <div className="mt-4 flex flex-wrap justify-center gap-2">
            <Button variant="page-primary" onClick={() => navigate('/playing')}>
              {t('playing.backHub')}
            </Button>
            {gameId && (
              <Button variant="page-secondary" onClick={() => window.location.reload()}>
                {t('playing.retry')}
              </Button>
            )}
          </div>
        </div>
      ) : (
        <div className="playing-game-layout">
          <div className="playing-board-card">
            {analysisOn && (
              <div className="playing-eval-bar-wrap mb-3" title={formatEvalLabel(currentEval)}>
                <div className="playing-eval-bar">
                  <div
                    className="playing-eval-bar-white"
                    style={{ width: `${evalBarPct(currentEval)}%` }}
                  />
                </div>
                <span className="playing-eval-label tabular-nums">
                  {analysisBusy && !currentEval
                    ? `${analysisProgress}%`
                    : formatEvalLabel(currentEval)}
                </span>
              </div>
            )}
            <PlayerClockBar
              name={top.name}
              userId={top.userId}
              rating={top.rating}
              ratingAfter={top.ratingAfter}
              captured={top.captured}
              materialAdv={top.materialAdv}
              clockMs={top.clock}
              active={top.active && !waitingToJoin}
              low={top.clock != null && top.clock < 30000 && top.clock >= 10000}
              critical={top.clock != null && top.clock < 10000}
              colorLabel={top.color}
            />

            <div
              className={`playing-board-frame ${highContrast ? 'playing-board-frame--high-contrast' : ''}`}
              ref={boardWrapRef}
              data-piece-set={pieceSet}
            >
              <Chessboard
                position={displayChess.fen()}
                onPieceDrop={onDrop}
                onPieceDragBegin={onPieceDragBegin}
                onSquareClick={onSquareClick}
                boardOrientation={orientation}
                boardWidth={boardWidth}
                showBoardNotation
                animationDuration={animDurationMs(animationSpeed, reducedMotion)}
                arePiecesDraggable={canDragPieces}
                onSquareRightClick={onSquareRightClick}
                // Disable library corner dialog — we use one centered .playing-promo-overlay.
                // Otherwise drag→promote shows the board picker, then onPieceDrop opens ours again.
                onPromotionCheck={() => false}
                customSquareStyles={squareStyles}
                customArrows={bookHintArrows}
                customPieces={customPieces}
                customDarkSquareStyle={{ backgroundColor: boardColors(boardTheme, highContrast).dark }}
                customLightSquareStyle={{ backgroundColor: boardColors(boardTheme, highContrast).light }}
                customBoardStyle={{
                  borderRadius: '12px',
                  boxShadow: '0 8px 28px rgba(15, 23, 42, 0.12)',
                }}
              />
              {gameMeta?.id != null && (
                <GameBoardCoach
                  boardRef={boardWrapRef}
                  gameId={gameMeta.id}
                  focusPly={activePly > 0 ? activePly : null}
                />
              )}
              {pendingPromotion && (
                <div className="playing-promo-overlay" role="dialog" aria-label={t('playing.choosePromotion')}>
                  <p className="playing-promo-title">{t('playing.choosePromotion')}</p>
                  <div className="playing-promo-choices">
                    {promoPieces.map((p) => (
                      <button
                        key={p.key}
                        type="button"
                        className="playing-promo-piece"
                        onClick={() => {
                          if (pendingPromotion.premove) {
                            setPremove({
                              from: pendingPromotion.from,
                              to: pendingPromotion.to,
                              promotion: p.key,
                            });
                            setPendingPromotion(null);
                            return;
                          }
                          submitMove(pendingPromotion.from, pendingPromotion.to, p.key);
                        }}
                      >
                        {p.label}
                      </button>
                    ))}
                  </div>
                  <button
                    type="button"
                    className="playing-promo-cancel"
                    onClick={() => setPendingPromotion(null)}
                  >
                    {t('playing.cancel')}
                  </button>
                </div>
              )}
              {!finished && !viewingPast && displayChess.isCheck() && (
                <div className="playing-check-badge">{t('playing.check')}</div>
              )}
              {viewingPast && (
                <button
                  type="button"
                  className="playing-wait-badge playing-wait-badge--action"
                  onClick={() => goToPly(null)}
                >
                  {t('playing.reviewingPly', { ply: activePly, total: fullHistory.length })}
                  <span className="ml-2 underline">{t('playing.backToLive')}</span>
                </button>
              )}
              {!finished && engineThinking && !viewingPast && (
                <div className="playing-wait-badge">{t('playing.engineThinking')}</div>
              )}
              {!finished && busy && !engineThinking && !viewingPast && (
                <div className="playing-wait-badge">{t('playing.moveSending')}</div>
              )}
              {firstMoveSecsLeft != null && !viewingPast && (
                <div className="playing-wait-badge">
                  {myTurn
                    ? t('playing.firstMoveYourTurn', {
                      seconds: firstMoveSecsLeft,
                      defaultValue: 'Make your first move — {{seconds}}s (then clocks start)',
                    })
                    : t('playing.firstMoveWaiting', {
                      seconds: firstMoveSecsLeft,
                      defaultValue: 'Waiting for first move — {{seconds}}s',
                    })}
                </div>
              )}
              {!finished && !myTurn && !spectateOnly && humanGame && !engineThinking && !busy && !viewingPast && firstMoveSecsLeft == null && (
                premove ? (
                  <button
                    type="button"
                    className="playing-wait-badge playing-wait-badge--action"
                    onClick={() => {
                      setPremove(null);
                      clearSelection();
                    }}
                  >
                    {t('playing.premoveArmed', { from: premove.from, to: premove.to })}
                    <span className="ml-2 underline">{t('playing.clearPremove')}</span>
                  </button>
                ) : (
                  <div className="playing-wait-badge">{t('playing.waitingOpponent')}</div>
                )
              )}
              {openingInfo && (
                <div className="playing-opening-chip" title={localizeOpeningName(openingInfo.name, t)}>
                  {openingInfo.eco}
                  {' · '}
                  {localizeOpeningName(openingInfo.name, t)}
                </div>
              )}
              {bookHintCandidates.length > 0 && (
                <div
                  className="playing-opening-chip"
                  title={t('playing.bookMovesHint', {
                    defaultValue: 'Club book suggestions (vs computer only)',
                  })}
                >
                  {t('playing.bookMoves', { defaultValue: 'Book' })}
                  {' · '}
                  {bookHintCandidates.map((c) => c.uci).join(', ')}
                </div>
              )}
              {sharedAnalysis && !analysisOn && (
                <button
                  type="button"
                  className="playing-wait-badge text-left hover:ring-2 hover:ring-sky-300"
                  onClick={() => {
                    if (sharedAnalysis.ply != null) {
                      goToPly(Number(sharedAnalysis.ply));
                    }
                    setAnalysisOn(true);
                  }}
                >
                  {t('playing.sharedAnalysis', { name: sharedAnalysis.sharedByName || '…' })}
                  {(sharedAnalysis.evalCp != null || sharedAnalysis.bestUci) && (
                    <span className="opacity-80">
                      {' · '}
                      {sharedAnalysis.evalCp != null
                        ? formatEvalLabel({ evalCp: sharedAnalysis.evalCp, bestMove: sharedAnalysis.bestUci })
                        : null}
                      {sharedAnalysis.evalCp != null && sharedAnalysis.bestUci ? ' · ' : null}
                      {sharedAnalysis.bestUci || null}
                    </span>
                  )}
                  <span className="ml-2 underline">
                    {t('playing.openSharedTip', { defaultValue: 'Open tip' })}
                  </span>
                </button>
              )}
              {finished && resultDismissed && !viewingPast && (
                <div className="playing-result-chip">
                  <span>{statusLabel}</span>
                  {reasonLabel ? <span className="opacity-80"> · {reasonLabel}</span> : null}
                  <button
                    type="button"
                    className="playing-result-chip-btn"
                    onClick={() => setResultDismissed(false)}
                  >
                    {t('playing.showResult')}
                  </button>
                </div>
              )}
              {showResultOverlay && (
                <div
                  className="playing-result-overlay"
                  role="dialog"
                  aria-label={statusLabel}
                  onClick={(e) => {
                    if (e.target === e.currentTarget) setResultDismissed(true);
                  }}
                >
                  <p className="text-lg font-bold text-white">{statusLabel}</p>
                  {reasonLabel && (
                    <p className="mt-1 text-sm text-white/85">{reasonLabel}</p>
                  )}
                  {gameMeta?.rated && myRatingLine && (
                    <p className="mt-2 text-sm font-semibold tabular-nums text-emerald-200">
                      {t('playing.yourRating')}: {myRatingLine.text}
                    </p>
                  )}
                  <div className="mt-4 flex flex-wrap justify-center gap-2">
                    <Button
                      variant="page-secondary"
                      onClick={() => setResultDismissed(true)}
                    >
                      {t('playing.dismissResult')}
                    </Button>
                    <Button
                      variant="page-secondary"
                      onClick={() => {
                        setResultDismissed(true);
                        goToPly(Math.max(0, fullHistory.length - 1));
                      }}
                    >
                      {t('playing.reviewMoves')}
                    </Button>
                    {fullHistory.length > 0 && (
                      <Button variant="page-secondary" onClick={() => runAnalysis('deep')} disabled={analysisBusy}>
                        {t('playing.analyze')}
                      </Button>
                    )}
                    {gameMeta?.id && (() => {
                      const ply = mistakePlies[0]
                        || mistakePliesFromQualities(
                          serverMovesToClubReview(gameMeta?.moves)?.qualities || {},
                        )[0];
                      if (!ply) return null;
                      return (
                        <Button
                          variant="page-primary"
                          onClick={() => {
                            try {
                              const pgn = exportPgnText();
                              if (pgn?.trim()) sessionStorage.setItem('playing.coachBoardPgn', pgn);
                            } catch {
                              /* ignore */
                            }
                            const params = new URLSearchParams({ ply: String(ply) });
                            const me = Number(getUserId());
                            const roles = (getRoles() || []).map((r) => String(r).toUpperCase().replace(/^ROLE_/, ''));
                            const staff = roles.some((r) => r === 'ADMIN' || r === 'MANAGER' || r === 'COACH');
                            const mine = me
                              && (Number(gameMeta.whiteUserId) === me || Number(gameMeta.blackUserId) === me);
                            // Staff keep gameId even from /spectate so club review stays available.
                            if (mine || staff) {
                              params.set('gameId', String(gameMeta.id));
                            }
                            navigate(`/playing/coach-board?${params.toString()}`);
                          }}
                        >
                          {t('playing.studyMistakes', { defaultValue: 'Study mistakes' })}
                        </Button>
                      );
                    })()}
                    {gameMeta?.gameType === 'VS_COMPUTER' && (
                      <Button variant="page-primary" onClick={rematchComputer}>
                        {t('playing.rematch')}
                      </Button>
                    )}
                    {humanGame && !spectateOnly && rematchInboxError && !incomingRematchChallenge && (
                      <p className="w-full text-center text-sm text-amber-200">
                        {rematchInboxError}
                        {' '}
                        <button
                          type="button"
                          className="font-semibold underline"
                          onClick={() => rematchInboxReloadRef.current?.()}
                        >
                          {t('playing.retry', { defaultValue: 'Retry' })}
                        </button>
                      </p>
                    )}
                    {humanGame && !spectateOnly && incomingRematchChallenge && (
                      <>
                        <p className="w-full text-center text-sm text-white/90">
                          {t('playing.rematchIncoming', {
                            name: incomingRematchChallenge.fromName || t('playing.player'),
                            defaultValue: '{{name}} wants a rematch',
                          })}
                        </p>
                        {!isParentRole && (
                        <Button variant="page-primary" disabled={rematchBusy} onClick={acceptIncomingRematch}>
                          {t('playing.acceptRematch', { defaultValue: 'Accept rematch' })}
                        </Button>
                        )}
                        <Button variant="page-secondary" disabled={rematchBusy} onClick={declineIncomingRematch}>
                          {t('playing.decline')}
                        </Button>
                      </>
                    )}
                    {humanGame && !spectateOnly && !incomingRematchChallenge && !isParentRole && (
                      <>
                        <Button
                          variant="page-primary"
                          disabled={rematchBusy || rematchSent}
                          onClick={rematchHuman}
                        >
                          {rematchSent
                            ? t('playing.rematchSent')
                            : t('playing.rematch')}
                        </Button>
                      </>
                    )}
                    <Button variant="page-secondary" onClick={() => navigate('/playing')}>
                      {t('playing.backHub')}
                    </Button>
                    {!isParentRole && (
                      <Button variant="page-secondary" onClick={() => navigate('/playing/challenge')}>
                        {t('playing.challengePlayer')}
                      </Button>
                    )}
                  </div>
                </div>
              )}
              {showWaitingOverlay && (
                <div
                  className="playing-result-overlay"
                  role="dialog"
                  aria-label={t('playing.waitingToJoinTitle')}
                  data-testid="playing-waiting-join"
                >
                  <p className="text-lg font-bold text-white">
                    {isChallengeInvitee
                      ? t('playing.challengeIncoming')
                      : t('playing.waitingToJoinTitle')}
                  </p>
                  <p className="mt-2 text-sm text-white/90">
                    {isChallengeInvitee
                      ? t('playing.waitingToJoinInviteeHint', {
                          name: waitingOpponentName || t('playing.player'),
                          defaultValue: 'Accept to start the game against {{name}}.',
                        })
                      : t('playing.waitingToJoinHint', {
                          name: waitingOpponentName || t('playing.player'),
                          defaultValue: 'Waiting for {{name}} to join…',
                        })}
                  </p>
                  {waitingOpponentName && (
                    <p className="mt-3 text-base font-semibold text-white">
                      <UserLink
                        id={waitingOpponentId}
                        name={waitingOpponentName}
                        playing
                        className="text-white hover:text-emerald-200"
                      />
                    </p>
                  )}
                  <div className="mt-4 flex flex-wrap justify-center gap-2">
                    {isChallengeInvitee && gameMeta?.pendingChallengeId && (
                      <>
                        {!isParentRole && (
                          <Button variant="page-primary" disabled={busy} onClick={onJoinChallenge}>
                            {t('playing.accept')}
                          </Button>
                        )}
                        <Button variant="page-secondary" disabled={busy} onClick={onDeclineChallengeFromBoard}>
                          {t('playing.decline')}
                        </Button>
                      </>
                    )}
                    {(isChallengeSender || (!isChallengeInvitee && canAbort)) && (
                      <Button variant="page-danger" disabled={busy} onClick={onAbort}>
                        {t('playing.cancelChallenge')}
                      </Button>
                    )}
                    <Button variant="page-secondary" onClick={leaveWaitingBoard}>
                      {t('playing.backHub')}
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {/* Your clock sits flush under the board (Lichess-style); scrubber/tools below. */}
            <PlayerClockBar
              name={bottom.name}
              userId={bottom.userId}
              rating={bottom.rating}
              ratingAfter={bottom.ratingAfter}
              captured={bottom.captured}
              materialAdv={bottom.materialAdv}
              clockMs={bottom.clock}
              active={bottom.active && !waitingToJoin}
              low={bottom.clock != null && bottom.clock < 30000 && bottom.clock >= 10000}
              critical={bottom.clock != null && bottom.clock < 10000}
              colorLabel={bottom.color}
              className="playing-player-bar--below-board"
            />

            <div className="playing-board-extras">
            <MovePlyNav
              totalPlies={fullHistory.length}
              activePly={activePly}
              viewingPast={viewingPast}
              canScrub={canScrub}
              currentSan={activePly > 0 ? fullHistory[activePly - 1] : ''}
              showMistakes={analysisOn && mistakePlies.length > 0}
              onStart={() => goToPly(0)}
              onPrev={() => stepPly(-1)}
              onNext={() => stepPly(1)}
              onEnd={() => goToPly(null)}
              onPrevMistake={() => jumpMistake(-1)}
              onNextMistake={() => jumpMistake(1)}
            />

            {!spectateOnly && !finished && !waitingToJoin && (
              <div className="playing-mobile-actions">
                <PlayingGameControls
                  busy={busy}
                  humanGame={humanGame}
                  plyCount={plyCount}
                  engineThinking={engineThinking}
                  canAbort={canAbort}
                  gameMeta={gameMeta}
                  confirmResign={confirmResign}
                  confirmAbort={confirmAbort}
                  onCancelConfirm={() => {
                    setConfirmResign(false);
                    setConfirmAbort(false);
                  }}
                  onResign={onResign}
                  onAbort={onAbort}
                  onDraw={onDraw}
                  onDeclineDraw={onDeclineDraw}
                  onClaimDraw={onClaimDraw}
                  onTakeback={onTakeback}
                  onDeclineTakeback={onDeclineTakeback}
                  onReport={onReportOpponent}
                  reportBusy={reportBusy}
                />
              </div>
            )}
            {reportNotice && !finished && (
              <PlayingBanner tone="success">{reportNotice}</PlayingBanner>
            )}

            <PlayingBoardTools
              onFlip={() => setFlipOverride((o) => (o === 'white' ? 'black' : o === 'black' ? 'white' : (baseOrientation === 'white' ? 'black' : 'white')))}
              onAnalyze={() => runAnalysis('deep')}
              canAnalyze={fullHistory.length > 0}
              analysisBusy={analysisBusy}
              analysisOn={analysisOn}
              analysisProgress={analysisProgress}
              soundMuted={soundMuted}
              onToggleSound={async () => {
                const next = !soundMuted;
                setSoundMuted(next);
                setPlayingMuted(next);
                const ok = await persistPrefs({ soundEnabled: !next });
                if (ok == null) {
                  setSoundMuted(!next);
                  setPlayingMuted(!next);
                }
              }}
              rematchSlot={(
                <PlayingRematchActions
                  finished={finished}
                  spectateOnly={spectateOnly}
                  humanGame={humanGame}
                  vsComputer={gameMeta?.gameType === 'VS_COMPUTER'}
                  rematchBusy={rematchBusy}
                  rematchSent={rematchSent}
                  rematchInboxError={rematchInboxError}
                  incomingRematchChallenge={incomingRematchChallenge}
                  isParentRole={isParentRole}
                  onRematchComputer={rematchComputer}
                  onRematchHuman={rematchHuman}
                  onAcceptRematch={acceptIncomingRematch}
                  onDeclineRematch={declineIncomingRematch}
                  onRetryInbox={() => rematchInboxReloadRef.current?.()}
                  t={t}
                />
              )}
            />
            </div>
          </div>

          <aside className="playing-side-panel space-y-4">
            <div className="playing-panel-card">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                {t('playing.gameInfo')}
              </p>
              <p className="mt-1 text-sm font-medium text-slate-800" aria-live="polite" aria-atomic="true">
                {boardLiveStatus}
              </p>
              <span className="sr-only" aria-live="assertive" aria-atomic="true">
                {boardLiveStatus}
              </span>
              {analysisOn && (
                <div className="mt-3 rounded-xl border border-slate-100 bg-gradient-to-b from-slate-50 to-white px-3 py-3 text-sm">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                      {t('playing.analysis')}
                      {analysisSummary?.depth != null && (
                        <span className="ml-1 font-normal normal-case text-slate-400">
                          · d{analysisSummary.depth}
                          {analysisSummary.source === 'cloud' ? ` · ${t('playing.analysisCloud')}` : ''}
                          {analysisSummary.source === 'club' ? ` · ${t('playing.clubReview', { defaultValue: 'Club review' })}` : ''}
                        </span>
                      )}
                    </p>
                  </div>
                  {analysisSummary && (
                    <p className="mt-1 text-xs text-slate-600">
                      {t('playing.accuracy')}:{' '}
                      <span className="font-semibold tabular-nums text-slate-900">
                        W {analysisSummary.whiteAccuracy ?? '—'}%
                      </span>
                      {' · '}
                      <span className="font-semibold tabular-nums text-slate-900">
                        B {analysisSummary.blackAccuracy ?? '—'}%
                      </span>
                      {analysisSummary.whiteAvgCpLoss != null && (
                        <span className="text-slate-400">
                          {' · '}ACPL {analysisSummary.whiteAvgCpLoss}/{analysisSummary.blackAvgCpLoss}
                        </span>
                      )}
                    </p>
                  )}
                  {Object.keys(analysisByPly).length > 1 && (
                    <svg className="playing-eval-spark mt-2" viewBox="0 0 160 36" width="100%" height="36" aria-hidden>
                      <line x1="0" y1="18" x2="160" y2="18" stroke="#e2e8f0" strokeWidth="1" />
                      <path
                        d={evalSparklinePath(analysisByPly, fullHistory.length)}
                        fill="none"
                        stroke="#4f46e5"
                        strokeWidth="1.75"
                        strokeLinecap="round"
                      />
                    </svg>
                  )}
                  <p className="mt-1 font-semibold tabular-nums text-slate-900">
                    {formatEvalLabel(currentEval)}
                    {currentEval?.quality && activePly > 0 && (
                      <span className={`playing-move-quality playing-move-quality--${currentEval.quality}`}>
                        {t(`playing.moveQuality.${currentEval.quality}`)}
                      </span>
                    )}
                  </p>
                  {currentEval?.bestMove && (
                    <p className="mt-0.5 text-xs text-slate-600">
                      {t('playing.bestMove')}:{' '}
                      <span className="font-mono">
                        {pvUciToSan(displayChess.fen(), [currentEval.bestMove])[0] || currentEval.bestMove}
                      </span>
                    </p>
                  )}
                  {currentEval?.lines?.length > 1 && (
                    <ul className="mt-2 space-y-0.5 text-xs text-slate-600">
                      {currentEval.lines.slice(0, 3).map((ln, i) => {
                        const sans = pvUciToSan(displayChess.fen(), ln.pv || [ln.uci]);
                        return (
                          <li key={`${ln.uci}-${i}`}>
                            <span className="text-slate-400">{i + 1}.</span>{' '}
                            <span className="font-medium text-slate-800">{sans.join(' ') || ln.uci}</span>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                  {analysisBusy && (
                    <p className="mt-1 text-xs text-slate-500">
                      {t('playing.analyzing', { pct: analysisProgress })}
                    </p>
                  )}
                </div>
              )}
              {rematchSent && (
                <PlayingBanner tone="success">
                  <p>{t('playing.rematchSentHint')}</p>
                  {pendingRematchChallengeId && (
                    <div className="mt-2">
                      <Button variant="page-secondary" disabled={rematchBusy} onClick={cancelPendingRematch}>
                        {t('playing.cancelChallenge')}
                      </Button>
                    </div>
                  )}
                </PlayingBanner>
              )}
              {myTurn && !finished && !engineThinking && !waitingToJoin && (
                <p className="mt-1 text-sm font-semibold text-emerald-700">{t('playing.yourTurn')}</p>
              )}
              {!myTurn && !finished && !spectateOnly && humanGame && !engineThinking && !waitingToJoin && (
                <>
                  <p className="mt-1 text-sm text-slate-500">{t('playing.waitingOpponent')}</p>
                  <p className="mt-0.5 text-xs text-slate-400">
                    {t('playing.premoveHint', {
                      defaultValue: 'You can premove while waiting.',
                    })}
                  </p>
                </>
              )}
              {engineThinking && !finished && (
                <p className="mt-1 text-xs font-medium text-slate-600">{t('playing.engineThinking')}</p>
              )}
              {spectateOnly && (
                <p className="mt-3 text-xs text-slate-500">{t('playing.spectatingHint')}</p>
              )}
              {gameMeta?.drawOfferedByOpponent && !finished && !spectateOnly && (
                <PlayingBanner tone="warning">{t('playing.opponentOffersDraw')}</PlayingBanner>
              )}
              {gameMeta?.drawOfferedByMe && !finished && (
                <p className="mt-3 text-xs text-slate-600">{t('playing.youOfferedDraw')}</p>
              )}
              {gameMeta?.takebackOfferedByOpponent && !finished && !spectateOnly && (
                <PlayingBanner tone="warning">{t('playing.opponentOffersTakeback')}</PlayingBanner>
              )}
              {gameMeta?.takebackOfferedByMe && !finished && (
                <p className="mt-3 text-xs text-slate-600">{t('playing.youOfferedTakeback')}</p>
              )}
            </div>

            {waitingToJoin && (
              <div className="playing-panel-card" data-testid="playing-waiting-join-panel">
                <p className="text-sm font-semibold text-slate-900">
                  {isChallengeInvitee
                    ? t('playing.challengeIncoming')
                    : t('playing.waitingToJoinTitle')}
                </p>
                <p className="mt-1 text-sm text-slate-600">
                  {isChallengeInvitee
                    ? t('playing.waitingToJoinInviteeHint', {
                        name: waitingOpponentName || t('playing.player'),
                        defaultValue: 'Accept to start the game against {{name}}.',
                      })
                    : t('playing.waitingToJoinHint', {
                        name: waitingOpponentName || t('playing.player'),
                        defaultValue: 'Waiting for {{name}} to join…',
                      })}
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {isChallengeInvitee && gameMeta?.pendingChallengeId && (
                    <>
                      {!isParentRole && (
                        <Button variant="page-primary" disabled={busy} onClick={onJoinChallenge}>
                          {t('playing.accept')}
                        </Button>
                      )}
                      <Button variant="page-secondary" disabled={busy} onClick={onDeclineChallengeFromBoard}>
                        {t('playing.decline')}
                      </Button>
                    </>
                  )}
                  {(isChallengeSender || (!isChallengeInvitee && canAbort)) && (
                    <Button variant="page-danger" disabled={busy} onClick={onAbort}>
                      {t('playing.cancelChallenge')}
                    </Button>
                  )}
                </div>
              </div>
            )}

            {!spectateOnly && !finished && !waitingToJoin && typeMoveOpen && (
              <div className="playing-panel-card">
                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500" htmlFor="playing-move-input">
                  {t('playing.typeMove')}
                </label>
                <form
                  className="mt-2 flex gap-2"
                  onSubmit={(e) => {
                    e.preventDefault();
                    submitTypedMove();
                  }}
                >
                  <input
                    id="playing-move-input"
                    type="text"
                    value={moveText}
                    onChange={(e) => setMoveText(e.target.value)}
                    disabled={!myTurn || busy || engineThinking || viewingPast}
                    placeholder={t('playing.typeMovePlaceholder')}
                    className="min-w-0 flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm font-mono text-slate-900 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100 disabled:bg-slate-50"
                    autoComplete="off"
                    spellCheck={false}
                  />
                  <Button type="submit" variant="page-secondary" disabled={!myTurn || busy || engineThinking || viewingPast || !moveText.trim()}>
                    {t('playing.playMove')}
                  </Button>
                </form>
              </div>
            )}

            {!spectateOnly && !finished && !waitingToJoin && (
              <div className="playing-panel-card playing-actions playing-actions--desktop">
                <PlayingGameControls
                  busy={busy}
                  humanGame={humanGame}
                  plyCount={plyCount}
                  engineThinking={engineThinking}
                  canAbort={canAbort}
                  gameMeta={gameMeta}
                  confirmResign={confirmResign}
                  confirmAbort={confirmAbort}
                  onCancelConfirm={() => {
                    setConfirmResign(false);
                    setConfirmAbort(false);
                  }}
                  onResign={onResign}
                  onAbort={onAbort}
                  onDraw={onDraw}
                  onDeclineDraw={onDeclineDraw}
                  onClaimDraw={onClaimDraw}
                  onTakeback={onTakeback}
                  onDeclineTakeback={onDeclineTakeback}
                  onReport={onReportOpponent}
                  reportBusy={reportBusy}
                />
              </div>
            )}
            {reportNotice && (
              <PlayingBanner tone="success">{reportNotice}</PlayingBanner>
            )}

            <div className="playing-panel-card">
              <div className="mb-2 flex items-center justify-between gap-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  {t('playing.moves')}
                </p>
                <span className="text-xs tabular-nums text-slate-400">
                  {fullHistory.length}
                </span>
              </div>
              <MoveList
                history={fullHistory}
                emptyLabel={t('playing.noMovesYet')}
                activePly={activePly}
                interactive={canScrub}
                onSelectPly={(ply) => goToPly(ply >= fullHistory.length ? null : ply)}
                qualitiesByPly={analysisOn ? qualitiesByPly : null}
              />
            </div>

            <PlayingGameOptions
              open={optionsOpen}
              onOpenChange={setOptionsOpen}
              onDownloadPgn={downloadPgn}
              canPgn={fullHistory.length > 0}
              onCopyLink={copyGameLink}
              copiedLink={copiedLink}
              canCopy={!!gameMeta?.id}
              onCloudAnalyze={runServerCloudAnalysis}
              canCloud={canUseCloudAnalysis}
              analysisBusy={analysisBusy}
              analysisOn={analysisOn}
              onCancelAnalysis={cancelAnalysis}
              onHideAnalysis={() => {
                cancelAnalysis();
                setAnalysisOn(false);
                setAnalysisByPly({});
                setAnalysisSummary(null);
                setQualitiesByPly({});
              }}
              onShareAnalysis={() => {
                if (!currentEval || !gameMeta?.id) return;
                shareChessAnalysis(gameMeta.id, {
                  ply: activePly,
                  evalCp: currentEval.evalCp ?? currentEval.scoreCp ?? null,
                  bestUci: currentEval.bestMove || currentEval.bestUci || null,
                  pv: currentEval.pv || currentEval.pvs?.[0] || [],
                })
                  .then(() => {
                    setError('');
                    setShareNotice(t('playing.shareAnalysisDone'));
                    setTimeout(() => setShareNotice(''), 2500);
                  })
                  .catch((e) => setError(apiErrorMessage(e, t('playing.shareAnalysisFailed'))));
              }}
              canShare={analysisOn && !analysisBusy && !!currentEval && !!gameMeta?.id}
              showBookHints={showBookHints}
              onToggleBookHints={() => {
                const next = !showBookHints;
                setShowBookHints(next);
                persistPrefs({ showBookHints: next });
              }}
              showBookToggle={String(gameMeta?.gameType || '').toUpperCase() === 'VS_COMPUTER'}
              typeMoveOpen={typeMoveOpen}
              onToggleTypeMove={() => setTypeMoveOpen((v) => !v)}
              showTypeMoveToggle={!spectateOnly && !finished && !waitingToJoin}
            />

            {humanGame && (
              <PlayingDisclosure
                className="playing-chat-card"
                title={t('playing.chat')}
                hint={spectateOnly ? t('playing.chatReadOnly') : t('playing.chatEmpty')}
                open={chatOpen}
                onOpenChange={setChatOpen}
                badge={chatMessages.length > 0 ? chatMessages.length : null}
              >
                <div className="playing-chat-log" role="log" aria-live="polite">
                  {chatMessages.length === 0 ? (
                    <p className="px-1 py-2 text-xs text-slate-400">{t('playing.chatEmpty')}</p>
                  ) : (
                    chatMessages.map((m) => (
                      <div
                        key={m.id}
                        className={`playing-chat-row ${m.mine ? 'playing-chat-row--mine' : ''}`}
                      >
                        <span className="playing-chat-name">
                          <UserLink
                            id={m.userId}
                            name={m.userName || t('playing.player')}
                            playing
                            className="text-inherit hover:text-primary-700"
                          />
                        </span>
                        <span className="playing-chat-body">{m.body}</span>
                      </div>
                    ))
                  )}
                  <div ref={chatEndRef} />
                </div>
                {chatError && (
                  <p className="mt-1 text-xs text-red-600">
                    {chatError}
                    {' '}
                    <button
                      type="button"
                      className="font-semibold underline"
                      onClick={() => chatReloadRef.current?.()}
                    >
                      {t('playing.retry', { defaultValue: 'Retry' })}
                    </button>
                  </p>
                )}
                {!spectateOnly && (
                  <form className="playing-chat-form mt-2" onSubmit={sendChat}>
                    <input
                      type="text"
                      className="playing-chat-input"
                      maxLength={280}
                      value={chatDraft}
                      onChange={(e) => setChatDraft(e.target.value)}
                      placeholder={t('playing.chatPlaceholder')}
                      disabled={chatBusy}
                      aria-label={t('playing.chat')}
                    />
                    <Button type="submit" variant="page-secondary" disabled={chatBusy || !chatDraft.trim()}>
                      {t('playing.chatSend')}
                    </Button>
                  </form>
                )}
              </PlayingDisclosure>
            )}

            <div className="flex flex-wrap gap-2 text-sm">
              <Link to="/playing" className="playing-text-link">{t('playing.backHub')}</Link>
              <span className="text-slate-300">·</span>
              <Link to="/playing/live" className="playing-text-link">{t('playing.liveGames')}</Link>
              <span className="text-slate-300">·</span>
              <Link to="/playing/games" className="playing-text-link">{t('playing.myGames')}</Link>
            </div>
          </aside>
        </div>
      )}

      {reportOpen && (
        <div
          className="fixed inset-0 z-[90] flex items-end justify-center bg-slate-900/50 p-4 sm:items-center"
          role="dialog"
          aria-modal="true"
          aria-labelledby="playing-report-title"
          onClick={() => { if (!reportBusy) setReportOpen(false); }}
        >
          <div
            className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-5 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id="playing-report-title" className="text-lg font-semibold text-slate-900">
              {t('playing.reportPlayer', { defaultValue: 'Report player' })}
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              {t('playing.reportPrompt', {
                defaultValue: 'Briefly describe why you are reporting this player (optional):',
              })}
            </p>
            <fieldset className="mt-4 space-y-2">
              <legend className="sr-only">{t('playing.reportReasonPrompt', { defaultValue: 'Report reason' })}</legend>
              {reportReasons.map((r) => (
                <label key={r.id} className="flex cursor-pointer items-start gap-2 text-sm text-slate-800">
                  <input
                    type="radio"
                    name="report-reason"
                    className="mt-1"
                    checked={reportReason === r.id}
                    onChange={() => setReportReason(r.id)}
                    disabled={reportBusy}
                  />
                  <span>{r.label}</span>
                </label>
              ))}
            </fieldset>
            <label className="mt-4 block text-sm text-slate-700">
              {t('playing.reportDetails', { defaultValue: 'Details (optional)' })}
              <textarea
                className="input mt-1"
                rows={3}
                maxLength={500}
                value={reportDetails}
                onChange={(e) => setReportDetails(e.target.value)}
                disabled={reportBusy}
              />
            </label>
            <div className="mt-5 flex flex-wrap justify-end gap-2">
              <Button variant="page-secondary" disabled={reportBusy} onClick={() => setReportOpen(false)}>
                {t('common.cancel', { defaultValue: 'Cancel' })}
              </Button>
              <Button variant="page-primary" disabled={reportBusy} onClick={submitReport}>
                {reportBusy
                  ? t('playing.submitting', { defaultValue: 'Submitting…' })
                  : t('playing.submitReport', { defaultValue: 'Submit report' })}
              </Button>
            </div>
          </div>
        </div>
      )}
      <PlayingConfirmDialog
        open={!!pendingMoveConfirm}
        message={
          pendingMoveConfirm
            ? t('playing.confirmMove', {
              defaultValue: `Play move ${pendingMoveConfirm.from}${pendingMoveConfirm.to}?`,
              from: pendingMoveConfirm.from,
              to: pendingMoveConfirm.to,
            })
            : ''
        }
        confirmLabel={t('playing.confirmMoveAction', { defaultValue: 'Play move' })}
        onConfirm={() => {
          const pending = pendingMoveConfirm;
          setPendingMoveConfirm(null);
          if (pending?.from && pending?.to) submitMove(pending.from, pending.to, 'q');
        }}
        onCancel={() => setPendingMoveConfirm(null)}
      />
    </div>
  );
}
